/**
 * PANTALLA DE COBRO — la joya de Softfi.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  QUÉ HACE QUE ESTA PANTALLA SEA DISTINTA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Todos los sistemas de venta saben cobrar en una moneda. El problema real de un
 * negocio latinoamericano es otro:
 *
 *   «Me deben 18 dólares. El cliente me da un billete de 20 y además me hace un
 *    Pago Móvil de 100 bolívares. ¿Cuánto le devuelvo, y en qué moneda?»
 *
 * Esta pantalla resuelve exactamente eso:
 *
 *  1. **Botones de un toque** para el caso más común: "todo en efectivo",
 *     "todo en bolívares". El 80% de las ventas se cobra sin escribir nada.
 *  2. **Se pueden apilar varios pagos** de distintas monedas y métodos.
 *  3. **El indicador de FALTA / SOBRA es enorme** y se actualiza al escribir.
 *  4. Cuando sobra dinero, Softfi **calcula el vuelto en la moneda que elijas**,
 *     ya redondeado como se cobra en la calle (en bolívares, hacia arriba y sin
 *     céntimos).
 *
 * Todo lo que se muestra aquí es un cálculo de apoyo. El backend recalcula con
 * aritmética exacta y rechaza la venta si no cuadra.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  Banknote,
  ChevronLeft,
  ChevronDown,
  Check,
  CreditCard,
  HandCoins,
  Plus,
  Smartphone,
  Tag,
  Trash2,
  Wallet,
  WifiOff,
  X,
} from "lucide-react";
import { Boton, Campo, cx, Etiquetado, Lista, Nota } from "@/components/ui";
import { Modal } from "@/components/Modal";
import { AtajosDeFecha, enDias, fechaEnPalabras } from "@/components/PiezasDeCuenta";
import { exentosComoDinero, InterruptoresDeImpuesto } from "./InterruptoresDeImpuesto";
import { mensajeDeError, SoftfiError } from "@/lib/api";
import { encolarVenta, leerClienteLocal } from "@/lib/db";
import { useCobroEnCurso } from "@/lib/cobroEnCurso";
import {
  clientes,
  codigosDescuento,
  metodosPago,
  negocio,
  pos,
  type VentaIn,
} from "@/lib/endpoints";
import {
  aPrincipal,
  comoFormato,
  comoFormatoJuntoA,
  formatMoney,
  explicarTasa,
  monedaPrincipal,
  MONEDA_POR_DEFECTO,
  montoParaCompletar,
  redondearMoneda,
  toleranciaPorRedondeo,
  toNumber,
} from "@/lib/money";
import type {
  DescuentoCalculado,
  PagoIn,
  PaymentMethod,
  Sale,
  Tax,
  TenantCurrency,
} from "@/lib/types";
import { useAuth } from "@/stores/auth";
import { redondearMonto, useCarrito, type TotalesCarrito } from "@/stores/carrito";
import { conElPrecioCobrado } from "./precioCobrado";
import { sucursalParaVender } from "@/stores/sucursal";
import { useConexion } from "@/stores/conexion";
import { useOffline } from "@/stores/offline";
import { aviso } from "@/stores/ui";
import { construirReciboLocal, fotoDeTasas } from "./reciboLocal";
import { CamposDelMetodo, faltanDatos } from "./CamposDePago";
import { calcularVuelto, parteEntera, repartirVuelto, type ParteDelVuelto } from "./vuelto";
import { recargoDeImpuestosDePedido } from "./impuestosDePedido";
import { SelectorDeCliente } from "./SelectorDeCliente";
import { plural } from "@/lib/texto";
import { fechaLocal } from "@/lib/fechas";
import { useLimpiarMonto, useFormatoDeMoneda } from "@/lib/pasoDeMoneda";

/** Una fila de pago que el cajero está armando. */
type FilaPago = {
  id: number;
  metodoId: string;
  moneda: string;
  monto: string;
  /**
   * Los datos que pide ese método: `{ telefono: "0412...", banco: "Banesco" }`.
   *
   * Antes esto era un solo campo `referencia` de texto libre. Con eso el cajero
   * acababa escribiendo "0412-1234567 / 12345678 / Banesco / 004521" todo
   * junto, y el dueño no podía buscar un pago por cédula ni imprimirlo bien.
   */
  datos: Record<string, string>;
  /**
   * Lo que dice la casilla vacía. Solo la usa la fila de «¿Cuánto pagó hoy?»
   * que se abre sola al cargar una venta a la cuenta del cliente.
   */
  pista?: string;
  /**
   * Un rótulo encima de la fila: «Inicial del cliente», «Lo que pagó hoy».
   * Dice qué es ese dinero cuando la venta es a crédito o con financiera.
   */
  etiqueta?: string;
};

let contador = 0;

const ICONOS: Record<string, typeof Wallet> = {
  cash: Banknote,
  card: CreditCard,
  transfer: Wallet,
  mobile_payment: Smartphone,
  crypto: Wallet,
  credit: Wallet,
  other: Wallet,
};

export function ModalPago({
  totales: totalesDelCarrito,
  monedas: listaMonedas,
  metodos,
  impuestosDePedido = [],
  conInterruptoresDeImpuesto = false,
  textoVolver = "Volver a la cuenta",
  onCerrar,
  onVendido,
  enviar,
  comoPanel = false,
}: {
  totales: TotalesCarrito;

  /**
   * Los impuestos que el cajero puede encender en esta venta (el IGTF).
   *
   * Se reciben ya filtrados a los activos y de alcance «pedido». Vacío cuando
   * el negocio no tiene ninguno — que es lo normal — y entonces nada de esto
   * aparece ni cambia un céntimo.
   */
  impuestosDePedido?: Tax[];

  /**
   * Dibujar aquí dentro los interruptores de esos impuestos.
   *
   * ⚠️ En la pantalla de vender va apagado, y no por ahorro: los dibuja el
   * **resumen del pedido**, que está justo al lado. Encendiéndolo también aquí
   * saldrían dos veces en la misma pantalla, y tocar uno movería el otro.
   *
   * En el salón sí: ahí el cobro es una ventana y la cuenta la trae la
   * comanda, así que no hay ningún resumen donde ponerlos.
   */
  conInterruptoresDeImpuesto?: boolean;

  /**
   * Qué dice el enlace de salir del cobro.
   *
   * ⚠️ No es un detalle de redacción: es a dónde se vuelve, y depende de dónde
   * esté metido esto. En la columna del PC la cuenta ya se ve al lado — lo que
   * vuelve es el catálogo. En la hoja del celular y en la mesa del salón sí se
   * vuelve a la cuenta, porque ahí no se está viendo.
   */
  textoVolver?: string;

  /**
   * Las monedas y los métodos llegan como propiedades (no se piden aquí) porque
   * vienen de la copia guardada en el dispositivo. Así el cobro funciona igual
   * con internet y sin él.
   */
  monedas: TenantCurrency[];
  metodos: PaymentMethod[];
  onCerrar: () => void;
  onVendido: (venta: Sale) => void;

  /**
   * Dibujarse como PANEL en vez de como ventana.
   *
   * ═══════════════════════════════════════════════════════════════════════════
   *  ⚠️ POR QUÉ NO ES OTRO COMPONENTE
   * ═══════════════════════════════════════════════════════════════════════════
   *
   * En la pantalla de vender el cobro ya no abre una ventana: la columna del
   * carrito, que está ahí al lado, se convierte en el cobro. Así el catálogo no
   * desaparece y se puede agregar lo que el cliente pidió a última hora sin
   * cancelar nada — que pasa todos los días y hoy obliga a empezar de nuevo.
   *
   * Pero el salón SÍ lo abre como ventana: ahí no hay columna de carrito, se
   * cobra desde la mesa.
   *
   * Podrían ser dos componentes. Serían dos cobros: mil setecientas líneas de lo
   * más delicado de Softfi —dos monedas a la vez, el vuelto, el redondeo del
   * bolívar, los datos del Pago Móvil— duplicadas, y el día que se arregle algo
   * en uno el otro se queda con el fallo. Es lo mismo que ya está escrito arriba
   * sobre `enviar`, y por eso esto es una propiedad y no un archivo nuevo: el
   * cobro es uno, y lo que cambia es la caja donde va metido.
   */
  comoPanel?: boolean;

  /**
   * Cómo se manda el cobro, cuando no es una venta del carrito.
   *
   * ═════════════════════════════════════════════════════════════════════════
   *  ⚠️ POR QUÉ ESTE HUECO, EN VEZ DE UNA PANTALLA DE COBRO APARTE
   * ═════════════════════════════════════════════════════════════════════════
   *
   * Lo que hay encima de esta línea son trescientas líneas de lo más delicado
   * de Softfi: cobrar en dos monedas a la vez, calcular el vuelto en la que
   * pida el cliente, aplicar el redondeo del bolívar, pedir los datos del
   * Pago Móvil. Una mesa de restaurante se cobra **exactamente igual** — la
   * mitad en efectivo y la otra en Pago Móvil es el pan de cada día.
   *
   * Copiar todo eso en una pantalla de «cobrar mesa» daría dos sitios donde
   * arreglar el mismo redondeo, y el día que se arregle uno solo, dos mesas
   * del mismo local darían dos vueltos distintos.
   *
   * Así que lo único que cambia es **a dónde se manda**: con `enviar`, en vez
   * de armar una venta con el carrito, se le pasan los pagos a quien sabe
   * cerrar esa cuenta.
   *
   * ⚠️ También se le pasan los impuestos que el cajero encendió, porque esos
   * viven aquí dentro cuando se cobra una mesa: en el carrito no pueden vivir,
   * o el IGTF de la mesa 4 se quedaría puesto para la siguiente venta del
   * mostrador. Van los IDENTIFICADORES, nunca el monto — el servidor lo
   * calcula sobre lo que se pagó en cada moneda.
   *
   * ⚠️ Sin `enviar`, la venta puede irse a la cola sin conexión. **Con
   * `enviar`, no**: una comanda vive en el servidor y sus líneas también, así
   * que no hay forma de cerrarla desde un aparato incomunicado. El error se
   * enseña y el mesero espera a que vuelva la señal — que es la verdad, y es
   * mejor que un recibo local de una cuenta que allá sigue abierta.
   */
  enviar?: (
    pagos: PagoIn[],
    clientUuid: string,
    impuestosDePedido: string[],
  ) => Promise<Sale>;
}) {
  // Mientras esto esté abierto hay un cobro a medias: la actualización sola
  // no recarga la página (ver `app/actualizacionSola.ts`).
  useCobroEnCurso();
  const carrito = useCarrito();
  const { user, tenant } = useAuth();
  const tenantId = tenant?.id ?? null;
  const enLinea = useConexion((s) => s.enLinea);
  const refrescarContadores = useOffline((s) => s.refrescarContadores);

  // ═══════════════════════════════════════════════════════════════════════
  //  ⚠️ EL FIADO NO ES UN MÉTODO DE COBRO MÁS
  // ═══════════════════════════════════════════════════════════════════════
  //
  // Por dentro sí lo es —es lo que permite fiar solo una parte y que el arqueo
  // de caja siga cuadrando— pero **aquí no se enseña junto a Efectivo y
  // Tarjeta**, y esa diferencia es todo el problema que había.
  //
  // Antes, fiar consistía en darse cuenta de que uno de los botones de la
  // parrilla se llamaba «Fiado». El dueño que activó la función fue a cobrar,
  // no vio nada que dijera «fiar», y concluyó que no funcionaba. Tenía razón:
  // una opción que hay que adivinar no existe.
  //
  // Ahora tiene su propio botón, con su propia palabra, y pide a quién.
  //
  // ⚠️ FIAR Y COBRAR CON CASHEA SON DOS COSAS DISTINTAS, AUNQUE LAS DOS SEAN
  // «crédito» por dentro.
  //
  //   · **Fiar** es un favor del negocio: «llévatelo y me pagas». No es una
  //     forma de pagar, es no pagar todavía — por eso tiene botón aparte y pide
  //     a quién.
  //
  //   · **Cashea** SÍ es una forma de pagar, y el cliente la presenta como
  //     presentaría una tarjeta. Tiene que estar en la lista, junto a Efectivo
  //     y Pago Móvil, o el cajero no la encuentra.
  //
  // Antes se escondían los dos por igual, así que un Cashea recién creado
  // desaparecía de la pantalla y no había forma de cobrarlo.
  const metodoFiado = useMemo(
    () =>
      metodos.find(
        (m) => m.type === "credit" && m.credito_quien_debe === "cliente" && m.is_active,
      ),
    [metodos],
  );
  /*
    La ficha del cliente que se eligió para esta venta.

    ⚠️ Sirve para no volver a pedir lo que Softfi ya sabe: si el método pide
    «cédula del titular» y «teléfono», y el cliente ya está elegido, esos dos
    campos se rellenan solos. Con cola detrás, teclear un dato que el sistema
    tiene delante es justo lo que hace que se deje vacío.

    ⚠️ Primero de la copia del aparato, que tiene a todos los clientes (la
    baja la caja para vender sin internet). Antes se buscaba su NOMBRE en el
    servidor y se miraba la primera página: con cincuenta «María» o más, la
    elegida podía no estar, y los datos no se rellenaban sin avisar. El
    servidor queda para el cliente recién creado que aún no está en la copia.
  */
  const { data: clienteDeLaVenta = null } = useQuery({
    queryKey: ["clientes", "uno", carrito.clienteId],
    queryFn: async () => {
      const id = carrito.clienteId!;
      const local = await leerClienteLocal(id).catch(() => undefined);
      if (local) return local;
      const r = await clientes.buscar({ q: carrito.clienteNombre ?? undefined });
      return r.items.find((c) => c.id === id) ?? null;
    },
    enabled: Boolean(carrito.clienteId),
    staleTime: 60_000,
  });

  const metodosCobro = useMemo(
    () => metodos.filter((m) => m.id !== metodoFiado?.id),
    [metodos, metodoFiado],
  );

  /*
    ═════════════════════════════════════════════════════════════════════════
     ⚠️ LAS FINANCIERAS: CASHEA Y PARECIDAS
    ═════════════════════════════════════════════════════════════════════════

    Se separan del fiado y del resto porque una venta con Cashea **no es un
    cobro, son dos**, y hasta ahora Softfi la trataba como uno solo:

      · La INICIAL la paga el cliente ahí mismo, con lo que traiga. Efectivo en
        dólares, pago móvil en bolívares, tarjeta. Si es efectivo, entra al
        cajón como cualquier otra venta.
      · El RESTO lo paga la financiera después, por transferencia y en SU
        moneda — con Cashea, bolívares.

    Lo dijo el dueño en una línea: «la primera cuota es con cualquier moneda y
    si es con efectivo pues entra a la caja, pero después con Cashea lo demás
    es en bs». Y no se podía configurar, porque un método admite UNA moneda.

    La salida no era otra casilla: era partir la venta.
  */
  const financieras = useMemo(
    () =>
      metodos.filter(
        (m) => m.type === "credit" && m.credito_quien_debe === "tercero" && m.is_active,
      ),
    [metodos],
  );

  /**
   * ¿Se están enseñando todos los métodos, o solo los de siempre?
   *
   * ⚠️ Arranca en falso y solo con muchos métodos se nota. Ver `formasVisibles`.
   */
  const [verTodasLasFormas, setVerTodasLasFormas] = useState(false);

  const activas = useMemo(() => listaMonedas.filter((m) => m.is_active), [listaMonedas]);
  const hayVariasMonedas = activas.filter((m) => m.accepts_payment).length > 1;

  /**
   * Las formas de cobrar que se enseñan de una vez, sin abrir nada.
   *
   * ═══════════════════════════════════════════════════════════════════════════
   *  ⚠️ CÓMO SE ARMA LA LISTA, Y POR QUÉ NO ES MÉTODOS × MONEDAS
   * ═══════════════════════════════════════════════════════════════════════════
   *
   * Cruzarlos daría filas que no existen —«Zelle en bolívares»— y con cuatro
   * métodos y dos monedas serían ocho botones para elegir uno.
   *
   * La regla sale de cómo se cobra de verdad:
   *
   *   · **El efectivo, una vez por moneda.** En Venezuela «efectivo en dólares»
   *     y «efectivo en bolívares» son dos decisiones distintas que se toman
   *     veinte veces al día. Son las dos que más se pulsan de toda la pantalla.
   *   · **Los demás, una sola vez**, en su moneda. Zelle es Zelle: si el método
   *     tiene moneda fija se usa esa, y si no, la principal.
   *
   * ⚠️ Las financieras NO entran aquí. Cobrar con Cashea no es una forma de
   * pago: es que el cliente no paga hoy. Van en su propio bloque, abajo, y
   * mezclarlas con estas era justo lo que hacía dudar de si «A crédito» era el
   * fiado del negocio o el de la financiera.
   */
  const formasDeCobro = useMemo(() => {
    const monedasQueCobran = activas.filter((m) => m.accepts_payment);
    const principalDeCobro =
      monedasQueCobran.find((m) => m.is_primary) ?? monedasQueCobran[0];
    if (!principalDeCobro) return [];

    const iconoDe = (tipo: PaymentMethod["type"]) => {
      if (tipo === "cash") return <Banknote size={16} />;
      if (tipo === "card") return <CreditCard size={16} />;
      if (tipo === "mobile_payment") return <Smartphone size={16} />;
      return <Wallet size={16} />;
    };

    const filas: {
      clave: string;
      metodo: PaymentMethod;
      moneda: TenantCurrency;
      icono: ReactNode;
    }[] = [];

    for (const metodo of metodosCobro) {
      // Las financieras tienen su propio bloque: aquí solo va lo que cobra hoy.
      if (metodo.type === "credit") continue;

      if (metodo.type === "cash" && !metodo.allowed_currency_code) {
        for (const moneda of monedasQueCobran) {
          filas.push({
            clave: `${metodo.id}:${moneda.currency_code}`,
            metodo,
            moneda,
            icono: iconoDe(metodo.type),
          });
        }
        continue;
      }

      const suya = metodo.allowed_currency_code
        ? monedasQueCobran.find((m) => m.currency_code === metodo.allowed_currency_code)
        : principalDeCobro;
      // Un método atado a una moneda que el negocio apagó no se puede cobrar:
      // enseñarlo sería un botón que da error al tocarlo.
      if (!suya) continue;

      filas.push({
        clave: `${metodo.id}:${suya.currency_code}`,
        metodo,
        moneda: suya,
        icono: iconoDe(metodo.type),
      });
    }

    return filas;
  }, [activas, metodosCobro]);

  /*
    ═══════════════════════════════════════════════════════════════════════════
     ⚠️ LOS ATAJOS SALEN DE LO QUE ESTE NEGOCIO USA, NO DE UN ORDEN FIJO
    ═══════════════════════════════════════════════════════════════════════════

    El servidor cuenta los pagos de los últimos treinta días por método Y
    moneda, y de ahí sale el orden. Una bodega que cobra casi todo en bolívares
    ve primero el efectivo en bolívares; una tienda que cobra por Zelle ve
    Zelle. Nadie configura nada.

    ⚠️ CUATRO, Y NO SEIS. El dueño lo midió a ojo sobre la pantalla: «a ese
    tamaño caben 6, 3 y 3, o prefieres 2 y 2; creo que lo mejor es 4, no muchos
    negocios tendrán 6 métodos». Y hay un motivo que se ve en su captura: con
    dos columnas el nombre ya se corta —«Efectivo …»— porque el importe en
    bolívares es largo. Con tres columnas se cortarían todos, y un atajo que no
    se puede leer no es un atajo.

    ⚠️ TREINTA DÍAS PARA QUE LOS BOTONES NO BAILEN. Un botón que se mueve de
    sitio en un mostrador es peligroso: el cajero pulsa por memoria muscular,
    sin mirar. Con una ventana corta, una semana rara reordenaría la pantalla el
    lunes. Con treinta días el orden cambia despacio — se adapta al negocio sin
    moverse debajo de los dedos. Ver `DIAS_DE_USO` en `pos/service.py`.
  */
  const CUANTOS_ATAJOS = comoPanel ? 4 : 4;

  const { data: masUsados } = useQuery({
    queryKey: ["metodos-pago", "mas-usados"],
    queryFn: metodosPago.masUsados,
    /*
      Una hora. Esto no cambia de una venta a la siguiente y pedirlo en cada
      cobro sería una consulta agrupada sobre los pagos del mes por cada vez
      que un cajero abre la pantalla.
    */
    staleTime: 60 * 60_000,
  });

  /** Todas las formas de cobro, de la más usada a la menos. */
  const formasPorUso = useMemo(() => {
    if (!masUsados?.length) return formasDeCobro;

    /*
      El puesto de cada par método+moneda según lo que se usa. Lo que no
      aparece en la lista —un método recién creado, o uno que no se usó en el
      mes— se va al final conservando el orden de siempre.
    */
    const puesto = new Map(
      masUsados.map((u, i) => [`${u.payment_method_id}:${u.currency_code}`, i]),
    );
    const alFinal = masUsados.length + formasDeCobro.length;

    return [...formasDeCobro]
      .map((f, orden) => ({
        f,
        orden,
        puesto: puesto.get(`${f.metodo.id}:${f.moneda.currency_code}`) ?? alFinal,
      }))
      .sort((a, b) => a.puesto - b.puesto || a.orden - b.orden)
      .map((x) => x.f);
  }, [masUsados, formasDeCobro]);

  const atajos = useMemo(
    () => formasPorUso.slice(0, CUANTOS_ATAJOS),
    [formasPorUso, CUANTOS_ATAJOS],
  );

  /*
    ═══════════════════════════════════════════════════════════════════════════
     ⚠️ «COBRAR EL RESTO» SALE DEL MISMO ORDEN QUE «COBRO RÁPIDO» (2026-10-01)
    ═══════════════════════════════════════════════════════════════════════════

    Antes era un botón por moneda —«el resto en dólares», «el resto en
    bolívares»— sin decir con qué método, y el método lo elegía Softfi a
    ciegas. Lo pidió el dueño: que se parezca al cobro rápido. Dos botones:

      · El primero, el EFECTIVO, en la moneda en que más lo usa el negocio:
        es con lo que se completa casi siempre un pago a medias.
      · El segundo, la forma de cobro que más usa el negocio y que NO sea
        efectivo (Pago Móvil, Zelle, punto…). Otro efectivo en otra moneda
        no aporta nada: para eso está el desplegable de la moneda.

    Ningún crédito, ni el fiado ni Cashea: `formasDeCobro` ya los deja fuera.
  */
  const formasParaElResto = useMemo(() => {
    const efectivo = formasPorUso.find((f) => f.metodo.type === "cash");
    const otra = formasPorUso.find((f) => f.metodo.type !== "cash");
    return [efectivo, otra].filter((f): f is NonNullable<typeof f> => f !== undefined);
  }, [formasPorUso]);
  const principal = monedaPrincipal(listaMonedas);
  const formato = principal ? comoFormato(principal) : MONEDA_POR_DEFECTO;
  const codigoPrincipal = principal?.currency_code ?? "";

  const [pagos, setPagos] = useState<FilaPago[]>([]);
  /**
   * El porcentaje de inicial que se está usando en ESTA venta.
   *
   * ⚠️ Vive aquí y no en el método porque con Cashea **depende del nivel del
   * cliente**, no del negocio: el mismo mostrador cobra 20 % a uno y 60 % al
   * siguiente. El método solo propone el más común.
   */
  const [inicialPct, setInicialPct] = useState("");
  /**
   * La inicial en la moneda principal, como la dice la app de la financiera
   * (2026-10-01). Ver el campo «Inicial que paga el cliente».
   */
  const [inicialMonto, setInicialMonto] = useState("");
  const limpiarMonto = useLimpiarMonto();
  /** Mientras se escribe en la inicial, el efecto no la pisa. */
  const [editandoPct, setEditandoPct] = useState(false);
  /**
   * La inicial que dice la app de la financiera, en la moneda principal.
   *
   * ═══════════════════════════════════════════════════════════════════════════
   *  ⚠️ NO ES LO QUE SUMAN LAS FILAS DEL MOSTRADOR (2026-10-12)
   * ═══════════════════════════════════════════════════════════════════════════
   *
   * Antes la inicial de arriba y la fila «Inicial del cliente» eran el mismo
   * número: escribir en una movía la otra. Lo encontró el dueño con un cliente
   * de verdad: la inicial era $26,88 y el cliente dio un billete de $30. Al
   * poner 30 en la fila, la inicial pasaba a 30 y Cashea pagaba menos; no había
   * forma de decir «la inicial es 26,88 y le doy 3,12 de vuelto».
   *
   * Ahora son dos cosas: esta es la inicial (lo que NO paga la financiera), y
   * las filas son lo que el cliente entregó. Si entregó de más, sobra, y sale
   * el vuelto como en cualquier venta. `null` = no hay financiera en la venta.
   */
  const [inicialObjetivo, setInicialObjetivo] = useState<number | null>(null);
  /**
   * El vuelto, en una o varias monedas (2026-10-12): «se le da 1 $ y el resto
   * en pesos». Ver `repartirVuelto` en `vuelto.ts`.
   */
  const [partesVuelto, setPartesVuelto] = useState<ParteDelVuelto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pidiendoCliente, setPidiendoCliente] = useState(false);

  // ═══════════════════════════════════════════════════════════════════════
  //  «SIEMPRE»: LOS DATOS SE PIDEN ANTES DE COBRAR, NO DESPUÉS
  // ═══════════════════════════════════════════════════════════════════════
  //
  // Es lo que hacen las cadenas grandes —una farmacia, un supermercado con
  // puntos— y el orden no es casual: **después de cobrar, el cliente ya guardó
  // la cartera y se está yendo**. Preguntarle ahí es perder el dato la mitad de
  // las veces, y quedar mal la otra mitad.
  //
  // Cada negocio decide en Clientes → Opciones. Por defecto NO se pide: una
  // bodega con 200 ventas al día no puede parar 200 veces.
  // Se lee de la copia que ya cargó `AppLayout`, no con una petición propia: el
  // POS tiene que funcionar sin internet, y una consulta que falla no puede
  // decidir si se pregunta o no. Sin copia se usa el valor por defecto, que es
  // el que no molesta a nadie.
  const { data: negocioCargado } = useQuery({
    queryKey: ["negocio"],
    queryFn: negocio.obtener,
    staleTime: 5 * 60 * 1000,
  });

  // El teléfono y la dirección de la sucursal donde se vende, para el recibo
  // de una venta SIN INTERNET (las demás los traen del servidor). Igual que
  // arriba, de la copia que ya cargó `AppLayout`; sin ella, los del negocio.
  const { data: sucursales } = useQuery({
    queryKey: ["sucursales"],
    queryFn: negocio.sucursales,
    staleTime: 5 * 60_000,
  });
  function laSucursal() {
    const elegida = sucursalParaVender(tenantId);
    return sucursales?.find((s) => (elegida ? s.id === elegida : s.is_default));
  }
  const cuandoPedirDatos =
    ((negocioCargado?.settings as Record<string, unknown> | undefined)
      ?.pedir_datos_cliente as string) ?? "al_fiar";
  const [yaPreguntoAlAbrir, setYaPreguntoAlAbrir] = useState(false);
  const [queriaFiar, setQueriaFiar] = useState(false);

  /**
   * ¿Esta venta es un apartado?
   *
   * ⚠️ Es un interruptor de la VENTA, no un método de pago. Lo que cambia no es
   * cómo entra el dinero —eso sigue igual, con su abono inicial y su saldo—
   * sino si la mercancía sale por la puerta hoy o se queda guardada.
   *
   * Por eso vive aquí arriba y no en la fila de métodos: mezclarlo con
   * «Efectivo, Zelle, Crédito» haría creer que es otra forma de cobrar.
   */
  /**
   * El cupón que el cliente trajo.
   *
   * ⚠️ Se guarda el TEXTO y el descuento que dijo el servidor, pero al vender
   * se manda SOLO el texto. El monto de aquí es para enseñarlo en pantalla; el
   * que se cobra lo vuelve a calcular el servidor. Si se mandara este, una
   * copia modificada de la aplicación podría regalarse la tienda.
   */
  const [cupon, setCupon] = useState("");
  const [cuponAplicado, setCuponAplicado] = useState<DescuentoCalculado | null>(null);
  const [errorCupon, setErrorCupon] = useState<string | null>(null);
  const [validandoCupon, setValidandoCupon] = useState(false);
  const [mostrandoCupon, setMostrandoCupon] = useState(false);
  const reajustarAlDescuento = useRef(false);

  /**
   * ⚠️ EL TOTAL, YA CON EL CUPÓN DESCONTADO.
   *
   * El cupón lo calcula el servidor, pero la pantalla tiene que enseñar lo que
   * de verdad se va a cobrar. Sin esto, el cajero veía «Total a cobrar $8,80»
   * con un cupón de −$1,32 aplicado justo encima: cobraba 8,80, el servidor
   * cobraba 7,48, y saltaba «sobran 1,32, registra el vuelto» — con el cliente
   * delante y sin entender nada.
   *
   * Se hace aquí, sobre el objeto entero, en vez de en las diez lecturas de
   * `totales.total` repartidas por el archivo: si mañana se añade una lectura
   * más, ya nace bien.
   */
  const totalConCupon = useMemo(() => {
    if (!cuponAplicado) return totalesDelCarrito;
    const rebaja = toNumber(cuponAplicado.descuento);
    return {
      ...totalesDelCarrito,
      descuentoTotal: totalesDelCarrito.descuentoTotal + rebaja,
      total: Math.max(0, totalesDelCarrito.total - rebaja),
    };
  }, [totalesDelCarrito, cuponAplicado]);

  /*
    ═══════════════════════════════════════════════════════════════════════════
     DÓNDE SE RECUERDA QUÉ IMPUESTOS ESTÁN PUESTOS
    ═══════════════════════════════════════════════════════════════════════════

    Vendiendo del carrito, en el carrito: el resumen está a la izquierda y el
    cobro a la derecha, y los dos tienen que ver lo mismo.

    ⚠️ Cobrando una mesa, AQUÍ Y SOLO AQUÍ, en un estado que muere con la
    ventana. Guardarlo en el carrito haría que el IGTF de la mesa 4 quedara
    puesto para la siguiente venta del mostrador — y eso no da ningún error,
    solo cobra de más el resto de la tarde. Es la misma trampa de la lista de
    precios, que ya la pisamos una vez.
  */
  const [impuestosDeLaCuenta, setImpuestosDeLaCuenta] = useState<string[]>([]);
  const seleccionDeImpuestos = enviar ? impuestosDeLaCuenta : carrito.impuestosPedido;

  function alternarImpuesto(id: string) {
    if (!enviar) {
      carrito.alternarImpuestoDePedido(id);
      return;
    }
    setImpuestosDeLaCuenta((antes) =>
      antes.includes(id) ? antes.filter((x) => x !== id) : [...antes, id],
    );
  }

  /**
   * Los impuestos de pedido que están encendidos ahora mismo.
   *
   * ⚠️ Se cruza con la lista de verdad y no se cree lo guardado. Un impuesto
   * que el dueño desactivó ayer puede seguir apuntado en el carrito guardado
   * de este dispositivo; si se cobrara, el servidor rechazaría la venta entera
   * con el cliente delante.
   */
  const encendidos = useMemo(
    () => impuestosDePedido.filter((t) => seleccionDeImpuestos.includes(t.id)),
    [impuestosDePedido, seleccionDeImpuestos],
  );

  /**
   * Cuánto suman los impuestos de pedido con unos pagos dados.
   *
   * ═══════════════════════════════════════════════════════════════════════════
   *  ⚠️ ESTA CUENTA TIENE QUE DAR EXACTAMENTE LO MISMO QUE EL SERVIDOR
   * ═══════════════════════════════════════════════════════════════════════════
   *
   * Está escrita dos veces a propósito — aquí y en `_impuestos_de_pedido` de
   * `pos/service.py` — porque el cajero tiene que ver el total ANTES de mandar
   * nada. Si las dos no coinciden, la venta se rechaza con «sobran 0,90»
   * delante del cliente y nadie entiende por qué.
   *
   * Las reglas, palabra por palabra, son las de allá:
   *
   *   · La base es lo que ENTRA en las monedas de ese impuesto, convertido a la
   *     moneda de referencia. El IGTF grava el pago en divisas, no la compra:
   *     de 100 pagados con 30 en efectivo y 70 en bolívares, alcanza a 30.
   *   · El vuelto NO se resta. Ver el comentario largo del servidor: restarlo
   *     sería un pelo más exacto y aquí no se puede calcular, porque el vuelto
   *     depende del total y el total dependeria del vuelto.
   *   · Se topa a lo debido. Es lo que hace que pagar con un billete de 200 una
   *     compra de 100 no pague el impuesto sobre 200.
   *   · Un impuesto sin monedas alcanza a la venta entera.
   *   · **Y esos van PRIMERO**: el IGTF grava lo que el cliente entrega, y lo
   *     que entrega ya lleva el IVA dentro. Con IVA 16 % e IGTF 3 % sobre 100,
   *     el IGTF son 3,48 (el 3 % de 116) y no 3,00.
   *   · **Lo exento sale de la base de cada uno**: la harina exenta de IVA no
   *     suma a la base del IVA, y sigue pagando IGTF si no está exenta de él.
   *     Ver `lib/exentos.ts`, que es la misma cuenta del servidor.
   */
  const recargoDePedidoCon = useCallback(
    (lista: { moneda: string; monto: string }[]): number => {
      if (encendidos.length === 0) return 0;

      const entradoPorMoneda: Record<string, number> = {};
      for (const p of lista) {
        const m = activas.find((x) => x.currency_code === p.moneda);
        const valor = aPrincipal(toNumber(p.monto), m?.current_rate ?? "0");
        entradoPorMoneda[p.moneda] = (entradoPorMoneda[p.moneda] ?? 0) + valor;
      }

      // La cuenta, aparte y con sus pruebas: ver `impuestosDePedido.ts`.
      return recargoDeImpuestosDePedido({
        encendidos,
        total: totalConCupon.total,
        entradoPorMoneda,
        exentoPorImpuesto: totalConCupon.exentoPorImpuesto,
        sumaDeLineas: totalConCupon.sumaDeLineas,
      });
    },
    [encendidos, totalConCupon.total, totalConCupon.exentoPorImpuesto, totalConCupon.sumaDeLineas, activas],
  );

  /**
   * El total de verdad: el del carrito más lo que añadan los impuestos de
   * pedido con los pagos que hay puestos ahora mismo.
   *
   * Se mete DENTRO de `totales` y no al lado porque este archivo lee
   * `totales.total` en una docena de sitios — el vuelto, lo que falta, el botón
   * de cobrar, las equivalencias. Poniéndolo aquí, todos nacen bien; al lado,
   * habría que acordarse de cada uno.
   */
  const totales = useMemo(() => {
    const recargo = recargoDePedidoCon(pagos);
    if (recargo <= 0) return { ...totalConCupon, recargoDePedido: 0 };
    return {
      ...totalConCupon,
      impuestoTotal: totalConCupon.impuestoTotal + recargo,
      total: redondearMonto(totalConCupon.total + recargo),
      recargoDePedido: recargo,
    };
  }, [totalConCupon, recargoDePedidoCon, pagos]);

  /**
   * Le pregunta al servidor si el código sirve y cuánto rebaja.
   *
   * ⚠️ El mensaje de error viene TAL CUAL del servidor y se enseña entero:
   * dice «venció el 15/08/2026» o «aplica desde 20,00 de compra», que es algo
   * que el cajero le puede leer al cliente en voz alta. Un «código inválido»
   * genérico le obliga a inventarse una explicación.
   */
  async function aplicarCupon() {
    const texto = cupon.trim();
    if (texto.length < 2) return;
    setValidandoCupon(true);
    setErrorCupon(null);
    try {
      const r = await codigosDescuento.validar(
        texto,
        String(totalesDelCarrito.subtotal),
        // ⚠️ Qué hay en el carrito. Sin esto, un código acotado a ciertos
        // productos o categorías se rechaza siempre: el servidor no puede saber
        // qué se lleva el cliente y no descuenta a ciegas.
        totalesDelCarrito.porLinea.map((l) => ({
          item_id: l.itemId,
          line_subtotal: String(l.base),
        })),
      );
      /*
        ⚠️ SI YA ESTABA COBRADO EXACTO, EL COBRO BAJA CON EL DESCUENTO
        (2026-09-25). Lo vio el dueño en una venta de $17,55: la cajera tocó
        «Pago Móvil» —que pone el total de ese momento, 15.016,83 Bs— y DESPUÉS
        aplicó PROMO20. El total bajó a $14,04 pero el Pago Móvil se quedó en
        el de antes, sobraron $3,51 y la pantalla los propuso como vuelto en
        efectivo: la venta se guardó con 3.003,36 Bs saliendo de una caja en la
        que no entró un solo billete.

        Si antes del código lo cobrado cuadraba con el total, se ajusta la
        última fila al total nuevo (lo hace el efecto de `reajustarAlDescuento`,
        cuando el total ya lleva el descuento). Si no cuadraba —el cliente dio
        un billete de 20—, no se toca: ese vuelto es de verdad.
      */
      reajustarAlDescuento.current =
        pagos.some((p) => toNumber(p.monto) > 0) &&
        !pagos.some(esCredito) &&
        Math.abs(diferencia) <= tolerancia;
      setCuponAplicado(r);
      setMostrandoCupon(false);
    } catch (e) {
      setErrorCupon(mensajeDeError(e));
    } finally {
      setValidandoCupon(false);
    }
  }

  const [esApartado, setEsApartado] = useState(false);
  const apartadosEncendidos = Boolean(
    (negocioCargado?.settings as Record<string, unknown> | undefined)?.apartado_enabled,
  );

  useEffect(() => {
    if (cuandoPedirDatos !== "siempre") return;
    if (yaPreguntoAlAbrir || carrito.clienteId) return;
    setYaPreguntoAlAbrir(true);
    setPidiendoCliente(true);
  }, [cuandoPedirDatos, yaPreguntoAlAbrir, carrito.clienteId]);

  /*
    ═══════════════════════════════════════════════════════════════════════════
     ⚠️ «SIEMPRE» NO ERA SIEMPRE: LA X LO SALTABA
    ═══════════════════════════════════════════════════════════════════════════

    Lo encontró el dueño: «aún no funciona lo de pedir al cliente obligado, aún
    se puede quitar con la x».

    Y era verdad. La pregunta se abría sola al entrar a cobrar, pero cerrarla
    solo la cerraba: detrás quedaba la pantalla de cobro entera, lista para
    confirmar la venta sin cliente. El ajuste que dice «siempre» valía tanto
    como uno que dijera «si quieres».

    Salir de la pregunta ahora es salir del cobro: se vuelve a vender, con el
    carrito intacto. Es lo único coherente — si el negocio decidió que toda
    venta lleva cliente, no puede haber una pantalla de cobrar sin él.

    ⚠️ TRES CASOS EN LOS QUE **NO** SE VUELVE, Y CADA UNO IMPORTA:

      · Con cliente ya elegido. Elegir a alguien cierra esta misma ventana, y
        devolver al catálogo justo ahí sería expulsar del cobro a quien acaba
        de hacer lo que se le pedía.

      · Pulsando «Fiar» sin cliente. Ahí la pregunta la abrió el cajero, no el
        ajuste: arrepentirse y cobrar en efectivo es legítimo.

      · Si el negocio no exige los datos. La X vuelve a ser una X.

    ⚠️ El cliente se lee con `getState()` y NO de `carrito.clienteId`. Elegir
    uno y cerrar pasa en el mismo instante, y la copia del componente todavía
    trae el valor de antes —vacío—: con ella, elegir cliente devolvía al
    catálogo, que es exactamente el fallo contrario.
  */
  function cerrarLaPregunta() {
    setPidiendoCliente(false);
    const hayCliente = Boolean(useCarrito.getState().clienteId);
    if (cuandoPedirDatos === "siempre" && !hayCliente && !queriaFiar) {
      onCerrar();
    }
    setQueriaFiar(false);
  }

  // ── Cálculo en vivo ─────────────────────────────────────────────────────
  const recibido = useMemo(() => {
    let suma = 0;
    for (const p of pagos) {
      const moneda = activas.find((m) => m.currency_code === p.moneda);
      suma += aPrincipal(toNumber(p.monto), moneda?.current_rate ?? "0");
    }
    return suma;
  }, [pagos, activas]);

  /**
   * La financiera que se está usando en esta venta, si hay alguna.
   *
   * Es lo que decide si se enseña el control del porcentaje: sin una fila suya
   * puesta, corregir un porcentaje no repartiría nada.
   */
  const financieraEnUso = useMemo(
    () => financieras.find((f) => pagos.some((p) => p.metodoId === f.id)),
    [financieras, pagos],
  );

  /**
   * Cuánto falta, en la moneda de referencia, si ese dinero se pone en `codigo`.
   *
   * ══════════════════════════════════════════════════════════════════════════
   *  ⚠️ CON IMPUESTO DE PEDIDO, «LO QUE FALTA» SE MUEVE AL RELLENARLO
   * ══════════════════════════════════════════════════════════════════════════
   *
   * El IGTF se calcula sobre lo que entra en dólares. Al poner dólares en la
   * casilla, el impuesto sube, y entonces falta un poco más que hace un
   * instante. Con una sola pasada, COMPLETAR dejaba el pago corto justo por el
   * importe del impuesto — y el botón de cobrar se quedaba apagado sin decir
   * por qué.
   *
   * Se repite la cuenta con el pago ya puesto hasta que deja de moverse. Tres
   * vueltas sobran: cada una reduce la diferencia al 3 % de la anterior.
   *
   * ⚠️ Lo usan los TRES sitios que dicen cuánto falta — el botón de completar,
   * el atajo de un toque y el renglón de «faltan ≈ 570 Bs». Si cada uno hiciera
   * su cuenta, la pantalla enseñaría tres números para lo mismo.
   */
  const loQueFaltaSiSePagaEn = useCallback(
    (codigo: string, previos: { moneda: string; monto: string }[], yaPagado: number): number => {
      const tasa = toNumber(
        activas.find((x) => x.currency_code === codigo)?.current_rate ?? "0",
      );
      const totalCon = (lista: { moneda: string; monto: string }[]) =>
        Math.max(0, redondearMonto(totalConCupon.total + recargoDePedidoCon(lista) - yaPagado));

      let falta = totalCon(previos);
      if (encendidos.length === 0 || tasa <= 0) return falta;

      for (let i = 0; i < 3; i++) {
        const siguiente = totalCon([
          ...previos,
          { moneda: codigo, monto: String(falta * tasa) },
        ]);
        const estable = Math.abs(siguiente - falta) < 0.005;
        falta = siguiente;
        if (estable) break;
      }
      return falta;
    },
    [activas, encendidos.length, totalConCupon.total, recargoDePedidoCon],
  );

  const diferencia = recibido - totales.total; // >0 sobra ; <0 falta
  const falta = Math.max(0, -diferencia);
  const sobra = Math.max(0, diferencia);

  /**
   * A partir de cuánto una diferencia deja de ser redondeo y pasa a ser dinero.
   *
   * ⚠️ NO ES UN NÚMERO FIJO, y aquí lo era: medio céntimo para todo. Eso
   * bloqueaba ventas normales — total $13,53 pagado en bolívares, Softfi cobra
   * 769 Bs porque 768,5 no se puede entregar, y sobran ocho milésimas de dólar.
   * La pantalla pedía elegir en qué moneda dar un vuelto de $0,00 y el botón de
   * confirmar se quedaba muerto sin decir por qué.
   *
   * Es la misma regla del servidor: un paso de redondeo de cada moneda usada.
   * Ver `toleranciaPorRedondeo`.
   */
  const tolerancia = useMemo(() => {
    const usadas = activas.filter((m) => pagos.some((p) => p.moneda === m.currency_code));
    // Sin pagos todavía se mide contra la moneda principal, que es la que va a
    // llevar el total.
    return toleranciaPorRedondeo(usadas.length > 0 ? usadas : activas.filter((m) => m.is_primary));
  }, [activas, pagos]);

  /*
    El vuelto, calculado contra LO QUE SE LE COBRÓ y no contra el precio exacto,
    en una o en varias monedas.

    ⚠️ La cuenta vive en `vuelto.ts`, no aquí. Es la que decide cuánto dinero
    sale del cajón, y dentro de un `useMemo` no se puede probar. Allí tiene sus
    pruebas, con los números exactos del cajero que reportó el fallo y con el
    caso del dueño: «13,5 $, se le da 1 $ y 0,5 en pesos».
  */
  const reparto = useMemo(
    () =>
      repartirVuelto({
        total: totales.total,
        recibido,
        pagos: pagos.map((p) => ({ moneda: p.moneda, monto: p.monto })),
        partes: partesVuelto,
        monedas: activas,
      }),
    [totales.total, recibido, pagos, partesVuelto, activas],
  );
  const montosVuelto = reparto.montos;

  /*
    Lo mismo en la moneda de referencia, que es lo que se enseña en grande.

    ⚠️ Sale del vuelto REAL y no del sobrante teórico. Enseñar «$0,02» arriba y
    entregar 10 Bs —que son $0,0125— es la contradicción que hacía dudar de las
    dos cifras a la vez.
  */
  const vueltoEnPrincipal = useMemo(
    () =>
      partesVuelto.length === 0
        ? sobra
        : partesVuelto.reduce(
            (suma, p, i) =>
              suma +
              aPrincipal(
                montosVuelto[i] ?? 0,
                activas.find((m) => m.currency_code === p.moneda)?.current_rate ?? "0",
              ),
            0,
          ),
    [partesVuelto, montosVuelto, activas, sobra],
  );

  /** Con qué se puede dar vuelto en una moneda: lo que da vuelto y sirve en ella. */
  const metodosDeVueltoEn = useCallback(
    (codigo: string) =>
      metodosCobro.filter(
        (m) =>
          m.can_give_change && (!m.allowed_currency_code || m.allowed_currency_code === codigo),
      ),
    [metodosCobro],
  );
  /** Las monedas en que se puede dar vuelto. */
  const monedasDeVuelto = useMemo(() => activas.filter((m) => m.allows_change), [activas]);

  function cambiarParteVuelto(id: number, cambios: Partial<ParteDelVuelto>) {
    setPartesVuelto((partes) =>
      partes.map((p) => {
        if (p.id !== id) return p;
        const nueva = { ...p, ...cambios };
        // Otra moneda: si el método no sirve en ella, el primero que sí.
        if (cambios.moneda && !metodosDeVueltoEn(cambios.moneda).some((m) => m.id === nueva.metodo)) {
          nueva.metodo = metodosDeVueltoEn(cambios.moneda)[0]?.id ?? "";
        }
        return nueva;
      }),
    );
  }

  /**
   * Dar una parte del vuelto en otra moneda.
   *
   * La parte de ahora pasa a escribirse y se propone su parte entera —de
   * $1,50, $1—, y la nueva se queda con lo que falta, en su moneda. Es el caso
   * que pidió el dueño tal cual: un dólar en billete y el medio en pesos.
   */
  function dividirVuelto() {
    setPartesVuelto((partes) => {
      if (partes.length === 0) return partes;
      const usadas = new Set(partes.map((p) => p.moneda));
      const otra =
        monedasDeVuelto.find((m) => !usadas.has(m.currency_code) && !m.is_primary) ??
        monedasDeVuelto.find((m) => !usadas.has(m.currency_code)) ??
        monedasDeVuelto[0];
      if (!otra) return partes;
      const i = partes.length - 1;
      const ultima = partes[i]!;
      const monedaUltima = activas.find((m) => m.currency_code === ultima.moneda);
      const ahora = montosVuelto[i] ?? 0;
      const propuesto = monedaUltima ? parteEntera(ahora, monedaUltima) : ahora;
      return [
        ...partes.slice(0, i),
        { ...ultima, monto: propuesto > 0 ? String(propuesto) : "" },
        {
          id: ++contador,
          moneda: otra.currency_code,
          metodo: metodosDeVueltoEn(otra.currency_code)[0]?.id ?? "",
          monto: "",
        },
      ];
    });
  }

  /*
    ═══════════════════════════════════════════════════════════════════════════
     ⚠️ UN VUELTO SIN HABER ENTRADO EFECTIVO SE CONFIRMA (2026-09-25)
    ═══════════════════════════════════════════════════════════════════════════

    Con Pago Móvil, tarjeta o transferencia lo normal es cobrar exacto: lo que
    se pasa de más casi siempre es un monto que se quedó viejo (ver
    `aplicarCupon`). La venta 000021 del dueño se guardó así, con un vuelto en
    efectivo que nadie entregó, y la caja quedó $3,51 por debajo.

    Puede ser de verdad —el cliente transfiere de más y se le da la diferencia
    en billetes—, así que no se prohíbe: se pregunta. Hasta que se confirme o
    se cobre el exacto, no se puede cobrar. Se vuelve a preguntar si cambia el
    monto que sobra.
  */
  const vueltoSinEfectivo =
    sobra > tolerancia &&
    !pagos.some(
      (p) =>
        toNumber(p.monto) > 0 && metodos.find((m) => m.id === p.metodoId)?.can_give_change,
    );
  const [vueltoConfirmado, setVueltoConfirmado] = useState(false);
  useEffect(() => {
    setVueltoConfirmado(false);
  }, [sobra]);
  const conQueSeCobro = [
    ...new Set(
      pagos
        .filter((p) => toNumber(p.monto) > 0)
        .map((p) => metodos.find((m) => m.id === p.metodoId)?.name)
        .filter(Boolean),
    ),
  ].join(" y ");

  /*
    ⚠️ LA MONEDA DEL VUELTO SE PROPONE SOLA: LA MISMA CON LA QUE PAGÓ.

    Es lo que pasa el 99 % de las veces —pagó en bolívares, se le devuelve en
    bolívares— y dejarlo en «Elige…» obligaba a un clic más en cada venta con
    vuelto, que es de las cosas que más se hacen en el día.

    Y arregla algo peor: mientras no había moneda elegida, la cifra grande
    enseñaba el sobrante teórico ($0,02) y la línea de abajo el vuelto de verdad
    (10 Bs, que son $0,01). Dos números para lo mismo en la misma pantalla.
  */
  useEffect(() => {
    // Sin vuelto, se olvida el reparto: el próximo vuelto se vuelve a proponer
    // entero, en la moneda con que se pague entonces.
    if (sobra <= tolerancia) {
      if (partesVuelto.length > 0) setPartesVuelto([]);
      return;
    }
    if (partesVuelto.length > 0) return;

    // La moneda del pago más grande: si pagó con dos, el vuelto sale de la que
    // más puso. Las filas de crédito no cuentan: Cashea no entrega billetes.
    const enEfectivo = [...pagos]
      .filter((p) => toNumber(p.monto) > 0 && !esCredito(p))
      .sort((a, b) => toNumber(b.monto) - toNumber(a.monto))[0];

    const preferida = enEfectivo?.moneda ?? activas.find((m) => m.is_primary)?.currency_code;
    const candidata = monedasDeVuelto.some((m) => m.currency_code === preferida)
      ? preferida
      : monedasDeVuelto[0]?.currency_code;
    if (candidata) {
      setPartesVuelto([
        { id: ++contador, moneda: candidata, metodo: metodosDeVueltoEn(candidata)[0]?.id ?? "", monto: "" },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sobra, tolerancia, partesVuelto.length, pagos, activas, monedasDeVuelto, metodosDeVueltoEn]);

  /**
   * Cuánto hay que poner en una moneda para completar exactamente lo que falta.
   *
   * Es la división que el cajero tendría que hacer de cabeza: "faltan 1,00 dólar,
   * ¿cuántos bolívares son?". Softfi la hace y además redondea como se cobra en
   * esa moneda.
   */
  function montoQueCompleta(codigo: string): string {
    if (falta <= tolerancia) return "";
    const m = activas.find((x) => x.currency_code === codigo);
    if (!m || toNumber(m.current_rate) <= 0) return "";
    // ⚠️ Hacia arriba, siempre. Ver `montoParaCompletar`: redondeando al más
    // cercano, el botón que dice COMPLETAR dejaba el pago corto.
    return String(montoParaCompletar(loQueFaltaSiSePagaEn(codigo, pagos, recibido), m));
  }

  /**
   * Cuánto tiene que decir ESTA casilla para que la venta quede cubierta.
   *
   * ══════════════════════════════════════════════════════════════════════════
   *  ⚠️ NO ES LO MISMO QUE «LO QUE FALTA», Y CONFUNDIRLOS BORRA DINERO
   * ══════════════════════════════════════════════════════════════════════════
   *
   * El botón REEMPLAZA lo que hay escrito en su casilla. Ponerle «lo que falta»
   * borra lo que ya había: con 10,00 escritos de una venta de 10,50, faltan
   * 0,50 — y el botón dejaba la casilla en 0,50, tirando los 10,00. Ahora
   * faltaban 10,00 en vez de 0,50 y el cajero, que solo quería redondear, veía
   * el pago desaparecer.
   *
   * Lo correcto es preguntar cuánto falta SIN CONTAR esta fila: eso es lo que
   * esta fila tiene que valer.
   *
   * Se veía poco porque hacía falta escribir una cantidad a medias. Con un
   * impuesto de pedido encendido pasa siempre: el atajo pone el total, el
   * impuesto lo sube, y «Completar» quedaba a un clic de vaciar el pago.
   */
  function montoQueCompletaLaFila(codigo: string, idFila: number): string {
    if (falta <= tolerancia) return "";
    const m = activas.find((x) => x.currency_code === codigo);
    if (!m || toNumber(m.current_rate) <= 0) return "";

    const otras = pagos.filter((x) => x.id !== idFila);
    const recibidoDeLasOtras = otras.reduce((acc, x) => {
      const moneda = activas.find((y) => y.currency_code === x.moneda);
      return acc + aPrincipal(toNumber(x.monto), moneda?.current_rate ?? "0");
    }, 0);

    return String(
      montoParaCompletar(loQueFaltaSiSePagaEn(codigo, otras, recibidoDeLasOtras), m),
    );
  }

  /**
   * Cuánto tiene que decir esta fila para que lo cobrado sea EXACTO, sobre o
   * falte. Es la cuenta de `montoQueCompletaLaFila` sin su condición: aquella
   * solo sirve cuando falta, y esta también baja una fila que se pasó.
   */
  function montoExactoDeLaFila(codigo: string, idFila: number): string {
    const m = activas.find((x) => x.currency_code === codigo);
    if (!m || toNumber(m.current_rate) <= 0) return "";
    const otras = pagos.filter((x) => x.id !== idFila);
    const recibidoDeLasOtras = otras.reduce((acc, x) => {
      const moneda = activas.find((y) => y.currency_code === x.moneda);
      return acc + aPrincipal(toNumber(x.monto), moneda?.current_rate ?? "0");
    }, 0);
    return String(
      montoParaCompletar(loQueFaltaSiSePagaEn(codigo, otras, recibidoDeLasOtras), m),
    );
  }

  /**
   * Deja lo cobrado exacto tocando la ÚLTIMA fila con dinero, que es la que
   * completaba. Si las demás ya cubren el total, esa fila sobra y se quita.
   */
  function dejarCobroExacto() {
    const ultima = [...pagos].reverse().find((p) => !esCredito(p) && toNumber(p.monto) > 0);
    if (!ultima) return;
    const exacto = montoExactoDeLaFila(ultima.moneda, ultima.id);
    if (exacto === "") return;
    setPagos((lista) =>
      toNumber(exacto) <= 0 && lista.length > 1
        ? lista.filter((x) => x.id !== ultima.id)
        : lista.map((x) => (x.id === ultima.id ? { ...x, monto: exacto } : x)),
    );
  }

  /*
    ═══════════════════════════════════════════════════════════════════════════
     ⚠️ CAMBIAR DE MONEDA NO CAMBIA CUÁNTO VALE EL PAGO (2026-10-01)
    ═══════════════════════════════════════════════════════════════════════════

    Al cambiar el método o la moneda de una fila, el número se quedaba tal cual
    y solo cambiaba la moneda: 10 Bs de Pago Móvil pasados a Zelle eran $10, y
    $40 en efectivo pasados a Pago Móvil eran 40 Bs. Lo vio el dueño:
    «1 $ = 1 Bs ❌ — 1 $ = 800 Bs ✅».

    Ahora el dinero se convierte a la tasa del día y la fila sigue valiendo lo
    mismo. Y si con esa fila el cobro estaba EXACTO, queda exacto en la moneda
    nueva con la cuenta de «Completar»: el impuesto de pedido (IGTF) depende de
    la moneda, y convertir a secas dejaría sobrando o faltando justo eso.

    Con un crédito en la venta (fiado o Cashea) se convierte a secas: esa fila
    se recalcula sola con lo que pague el mostrador.
  */
  function cambiarFila(fila: FilaPago, cambios: Partial<FilaPago>) {
    const nueva = cambios.moneda;
    let monto: string | undefined;
    if (nueva && nueva !== fila.moneda && cambios.monto === undefined && toNumber(fila.monto) > 0) {
      const antes = activas.find((m) => m.currency_code === fila.moneda);
      const despues = activas.find((m) => m.currency_code === nueva);
      if (antes && despues && toNumber(antes.current_rate) > 0 && toNumber(despues.current_rate) > 0) {
        const estabaExacto = !pagos.some(esCredito) && Math.abs(diferencia) <= tolerancia;
        monto = estabaExacto
          ? montoExactoDeLaFila(nueva, fila.id)
          : String(
              redondearMoneda(
                aPrincipal(toNumber(fila.monto), antes.current_rate) * toNumber(despues.current_rate),
                despues,
              ),
            );
      }
    }
    setPagos((lista) =>
      lista.map((x) =>
        x.id === fila.id ? { ...x, ...cambios, ...(monto !== undefined ? { monto } : {}) } : x,
      ),
    );
  }

  // Ver el comentario de `aplicarCupon`: corre cuando el total ya lleva el
  // descuento, que es cuando la cuenta de arriba da el monto nuevo.
  useEffect(() => {
    if (!reajustarAlDescuento.current) return;
    reajustarAlDescuento.current = false;
    dejarCobroExacto();
  }, [cuponAplicado]);

  /*
    ═══════════════════════════════════════════════════════════════════════════
     ⚠️ LO QUE QUEDA DEBIENDO SE CALCULA SOLO (lo pidió el dueño, 2026-09-25)
    ═══════════════════════════════════════════════════════════════════════════

    La fila de «Queda debiendo» nacía con el total y se quedaba así. Si el
    cliente pagaba una parte, la cajera tenía que restar de cabeza —compra de
    48, paga 27, ¿cuánto queda?— y escribirlo; y si en vez de eso agregaba el
    efectivo con «Dividir», la deuda seguía en el total y la pantalla pedía
    dar de vuelto justo lo que el cliente había pagado.

    Ahora es al revés, que es como se piensa en el mostrador: se pregunta
    cuánto PAGÓ y la deuda es lo que falta. Esta fila no se escribe: vale
    siempre el total menos las demás, con la misma cuenta que «Completar»
    (impuesto de pedido incluido). Si paga todo, queda en cero y no se manda.
  */
  //
  // ⚠️ Y VALE PARA TODO CRÉDITO, no solo el del cliente: con Cashea la fila
  // de la financiera es el total menos la INICIAL. Si la cajera corrige la
  // inicial —la app de Cashea del cliente dice otro monto—, lo que paga
  // Cashea se ajusta solo, y el porcentaje de arriba también.
  useEffect(() => {
    const fila = pagos.find((p) => metodos.find((m) => m.id === p.metodoId)?.type === "credit");
    if (!fila) return;
    const m = activas.find((x) => x.currency_code === fila.moneda);
    if (!m || toNumber(m.current_rate) <= 0) return;

    const otras = pagos.filter((x) => x.id !== fila.id);
    const recibidoDeLasOtras = otras.reduce((acc, x) => {
      const moneda = activas.find((y) => y.currency_code === x.moneda);
      return acc + aPrincipal(toNumber(x.monto), moneda?.current_rate ?? "0");
    }, 0);

    /*
      ⚠️ LA FINANCIERA PAGA EL TOTAL MENOS LA INICIAL DE SU APP, no menos lo que
      entregó el cliente (2026-10-12). Con un billete de $30 para una inicial de
      $26,88, Cashea sigue pagando lo mismo y sobran $3,12 de vuelto. Ver
      `inicialObjetivo`.

      El fiado sí es el total menos lo que pagó hoy: ahí no hay una inicial que
      diga nadie, y lo que pague el cliente es lo que deja de deber.
    */
    const esFinanciera = metodos.find((x) => x.id === fila.metodoId)?.credito_quien_debe === "tercero";
    if (esFinanciera && inicialObjetivo === null) {
      // Una fila pasada a Cashea a mano: la inicial es lo que ya hay en el mostrador.
      setInicialObjetivo(Math.min(recibidoDeLasOtras, totales.total));
      return;
    }
    const yaPagado = esFinanciera ? Math.min(inicialObjetivo ?? 0, totales.total) : recibidoDeLasOtras;
    const debe = String(montoParaCompletar(loQueFaltaSiSePagaEn(fila.moneda, otras, yaPagado), m));
    if (toNumber(debe) !== toNumber(fila.monto) || fila.monto === "") {
      setPagos((lista) => lista.map((x) => (x.id === fila.id ? { ...x, monto: debe } : x)));
    }

    // El porcentaje de la inicial dice cuánto es de la venta. Mientras se
    // escribe en ella no se toca: borrarla para poner otro número la devolvería
    // a la de antes a mitad de camino.
    if (esFinanciera && !editandoPct && totales.total > 0 && inicialObjetivo !== null) {
      // Nunca más de 100: «1042 %» no es una inicial (lo vio el dueño).
      const real = String(Math.min(100, Math.round((inicialObjetivo / totales.total) * 100)));
      if (real !== inicialPct) setInicialPct(real);
      const monto = String(Math.round(Math.min(inicialObjetivo, totales.total) * 100) / 100);
      if (monto !== inicialMonto) setInicialMonto(monto);
    }
  }, [pagos, metodos, activas, loQueFaltaSiSePagaEn, editandoPct, totales.total, inicialPct, inicialMonto, inicialObjetivo]);

  // Sin financiera en la venta, no hay inicial que recordar.
  useEffect(() => {
    if (!financieraEnUso && inicialObjetivo !== null) setInicialObjetivo(null);
  }, [financieraEnUso, inicialObjetivo]);

  /**
   * Lo que falta (o sobra), expresado en cada moneda que el negocio acepta.
   *
   * ⚠️ Cuando SOBRA, cada moneda usa la misma cuenta que el vuelto de verdad.
   *
   * Antes esta línea convertía el sobrante teórico, así que decía «= 15,00 Bs»
   * justo encima de un vuelto de 10. Dos números distintos para lo mismo, en la
   * misma pantalla, y ninguno de los dos era de fiar a partir de ahí.
   */
  const equivalencias = useMemo(() => {
    if (falta > tolerancia) {
      /*
        ⚠️ La MISMA cuenta que el botón de completar, no una parecida.

        Este renglón dice «faltan ≈ 570 Bs» justo encima de un botón que pone
        otra cifra en la casilla. Dos números distintos para lo mismo, en la
        misma pantalla, es peor que no enseñar ninguno.
      */
      return activas
        .filter((m) => !m.is_primary && toNumber(m.current_rate) > 0)
        .map((m) => ({
          moneda: m,
          monto: montoParaCompletar(
            loQueFaltaSiSePagaEn(m.currency_code, pagos, recibido),
            m,
          ),
        }));
    }
    if (sobra <= tolerancia) return [];

    return activas
      .filter((m) => !m.is_primary && toNumber(m.current_rate) > 0)
      .map((m) => ({
        moneda: m,
        monto: calcularVuelto({
          total: totales.total,
          recibido,
          pagos: pagos.map((p) => ({ moneda: p.moneda, monto: p.monto })),
          monedaVuelto: m,
        }),
      }));
  }, [activas, falta, sobra, totales.total, recibido, pagos, loQueFaltaSiSePagaEn]);

  // ── Acciones ────────────────────────────────────────────────────────────

  /*
    ⚠️ LAS FILAS SE DESLIZAN AL CAMBIAR DE SITIO (lo pidió el dueño).

    Al dividir con un crédito puesto, la fila nueva entra ANTES del crédito, y
    sin animación el crédito «salta» de sitio y parece que se cambió otra
    cosa. Se mide dónde estaba cada fila en la pintada anterior y, si cambió
    el orden, cada una arranca desde su sitio viejo y se desliza al nuevo; la
    que entra, aparece suave. Se mide contra la lista (`offsetTop`), no contra
    la ventana: si no, desplazar la página parecería un cambio de sitio.

    Sin animaciones si el aparato pide movimiento reducido, y sin nada en las
    pruebas (jsdom no tiene `animate`).
  */
  const cajasDeFilas = useRef(new Map<number, HTMLDivElement>());
  const posicionesAntes = useRef<{ firma: string; tops: Map<number, number> }>({
    firma: "",
    tops: new Map(),
  });
  const firmaDeFilas = pagos.map((f) => f.id).join(",");
  useLayoutEffect(() => {
    const tops = new Map<number, number>();
    cajasDeFilas.current.forEach((el, id) => tops.set(id, el.offsetTop));
    const antes = posicionesAntes.current;
    const reducir =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (antes.firma !== firmaDeFilas && antes.tops.size > 0 && !reducir) {
      cajasDeFilas.current.forEach((el, id) => {
        if (typeof el.animate !== "function") return;
        const previo = antes.tops.get(id);
        const ahora = tops.get(id) ?? 0;
        if (previo === undefined) {
          el.animate(
            [
              { opacity: 0, transform: "translateY(-8px)" },
              { opacity: 1, transform: "none" },
            ],
            { duration: 220, easing: "ease-out" },
          );
        } else if (Math.abs(previo - ahora) > 1) {
          el.animate(
            [{ transform: `translateY(${previo - ahora}px)` }, { transform: "none" }],
            { duration: 320, easing: "cubic-bezier(0.2, 0.8, 0.2, 1)" },
          );
        }
      });
    }
    posicionesAntes.current = { firma: firmaDeFilas, tops };
  });

  /** ¿Esta fila es un crédito (del cliente o de una financiera)? */
  function esCredito(f: FilaPago): boolean {
    return metodos.find((m) => m.id === f.metodoId)?.type === "credit";
  }

  function agregarPago(metodoId: string, moneda: string, monto = "") {
    setPagos((p) => {
      const nueva: FilaPago = { id: ++contador, metodoId, moneda, monto, datos: {} };
      // ⚠️ ANTES del crédito, que va siempre de último: se calcula con todo lo
      // demás, así que es la última cuenta. Al entrar la nueva, la del crédito
      // baja deslizándose (ver `useLayoutEffect` de las filas).
      const i = p.findIndex(esCredito);
      return i < 0 ? [...p, nueva] : [...p.slice(0, i), nueva, ...p.slice(i)];
    });
  }

  /**
   * Fiar lo que falte.
   *
   * Si la venta todavía no tiene cliente, primero pregunta a quién — porque una
   * deuda sin dueño no se puede cobrar, y el backend la rechaza por eso mismo.
   * Vale la pena que lo pregunte la pantalla y no que lo conteste un error
   * rojo con el cliente delante.
   */
  function fiarElResto() {
    if (!metodoFiado) return;
    if (!carrito.clienteId) {
      setQueriaFiar(true);
      setPidiendoCliente(true);
      return;
    }
    ponerFiado();
  }

  function ponerFiado() {
    if (!metodoFiado || falta <= 0.005) return;
    setPagos((p) => {
      // Se quita cualquier fiado anterior: dos filas de fiado en la misma
      // venta no significan nada, y sumarían el doble de deuda.
      const sinFiado = p.filter((f) => f.metodoId !== metodoFiado.id);

      // ⚠️ Y SE PREGUNTA CUÁNTO PAGÓ HOY. Si no hay ningún otro pago, se abre
      // una casilla vacía para anotarlo —con el cursor ya puesto— y la deuda
      // se calcula sola con lo que se escriba. Vacía no se manda: si no pagó
      // nada, todo queda a crédito.
      const metodoDeHoy =
        sinFiado.length === 0 ? elegirMetodo(metodosCobro ?? [], codigoPrincipal) : undefined;
      const pagoDeHoy: FilaPago[] = metodoDeHoy
        ? [
            {
              id: ++contador,
              metodoId: metodoDeHoy.id,
              moneda: codigoPrincipal,
              monto: "",
              datos: {},
              pista: "¿Cuánto pagó hoy?",
              etiqueta: "Lo que pagó hoy",
            },
          ]
        : [];

      return [
        ...sinFiado,
        ...pagoDeHoy,
        // El monto lo pone el efecto de arriba: lo que falta.
        { id: ++contador, metodoId: metodoFiado.id, moneda: codigoPrincipal, monto: "", datos: {} },
      ];
    });
  }

  /*
    ═══════════════════════════════════════════════════════════════════════════
     ⚠️ COBRAR CON UNA FINANCIERA ES PARTIR LA VENTA EN DOS
    ═══════════════════════════════════════════════════════════════════════════

    Y es TODO el arreglo de Cashea. Antes esto no lo hacía nadie: el cajero
    tenía que darse cuenta solo de que hacían falta dos filas, calcular la
    inicial a mano y acordarse de ponerla en la moneda correcta. En una cola,
    con el cliente delante, eso no pasa — se cobraba todo a Cashea y la inicial
    que el cliente había puesto sobre el mostrador no aparecía en ninguna parte.

    Peor todavía: existía un campo de texto llamado «Cuota inicial» donde el
    cajero escribía 20 creyendo que lo estaba cobrando. Ese 20 no lo leía nadie.

    Ahora es un toque:

      · Fila 1 — la inicial, en efectivo y en la moneda principal. Es lo más
        común y lo que menos hay que corregir; si el cliente paga con pago móvil
        o en otra moneda, se cambia en su desplegable como cualquier otro pago.
      · Fila 2 — el resto, a nombre de la financiera y en LA MONEDA EN QUE ELLA
        PAGA (`allowed_currency_code`). Ahí es donde deja de importar en qué
        moneda pagó el cliente su inicial: son cobros distintos.

    El porcentaje llega del método, pero se puede corregir en el mostrador
    salvo que el dueño lo haya fijado — con Cashea depende del NIVEL DEL
    CLIENTE, no del negocio, y eso el cajero lo lee en el teléfono del cliente
    cuando ya lo tiene delante.
  */
  /*
    ⚠️ CAMBIAR EL PORCENTAJE NO REHACE LA VENTA.

    Lo encontró el dueño: pagó la inicial con dólares y pesos, cambió el
    porcentaje a 40 y la fila de los pesos desapareció. Cada cambio de
    porcentaje volvía a armar las filas desde cero.

    Ahora, con la financiera ya puesta, solo se ajusta la fila «Inicial del
    cliente» para que la suma de todo lo del mostrador dé el porcentaje: 40 %
    de 9,60 son 3,84; si 10.000 pesos ya valen 3,69, en dólares queda 0,15.
    Lo que paga la financiera lo recalcula solo el efecto de arriba.
  */
  /** `objetivo`: la inicial en la moneda principal (de un porcentaje o escrita tal cual). */
  function ajustarInicial(objetivo: number) {
    const tasaDe = (codigo: string) =>
      toNumber(activas.find((m) => m.currency_code === codigo)?.current_rate ?? "0");

    setPagos((lista) => {
      const delMostrador = lista.filter((f) => !esCredito(f));
      const inicial =
        delMostrador.find((f) => f.etiqueta === "Inicial del cliente") ?? delMostrador[0];
      const enLasOtras = delMostrador
        .filter((f) => f !== inicial)
        .reduce((acc, x) => acc + aPrincipal(toNumber(x.monto), String(tasaDe(x.moneda))), 0);
      const falta = Math.max(0, objetivo - enLasOtras);

      if (!inicial) {
        // La quitaron: si el porcentaje pide inicial, vuelve arriba del todo.
        const moneda = activas.find((m) => m.currency_code === codigoPrincipal) ?? activas[0];
        const metodoIni = moneda ? elegirMetodo(metodosCobro, moneda.currency_code) : undefined;
        if (falta <= 0.005 || !moneda || !metodoIni) return lista;
        return [
          {
            id: ++contador,
            metodoId: metodoIni.id,
            moneda: moneda.currency_code,
            monto: String(redondearMoneda(falta * toNumber(moneda.current_rate), moneda)),
            datos: {},
            etiqueta: "Inicial del cliente",
          },
          ...lista,
        ];
      }

      const moneda = activas.find((m) => m.currency_code === inicial.moneda);
      if (!moneda) return lista;
      const monto = String(redondearMoneda(falta * toNumber(moneda.current_rate), moneda));
      return lista.map((f) => (f.id === inicial.id ? { ...f, monto } : f));
    });
  }

  function cobrarConFinanciera(metodo: PaymentMethod, pct?: number) {
    const porcentaje = Math.min(100, pct ?? Number(metodo.credito_inicial_pct) ?? 0);
    // Ya se está cobrando con ella: solo se mueve la inicial, sin tocar nada más.
    if (pct !== undefined && pagos.some((f) => f.metodoId === metodo.id)) {
      ajustarInicial((totales.total * porcentaje) / 100);
      setInicialObjetivo((totales.total * porcentaje) / 100);
      return;
    }
    // Sobre el total de la venta, en la moneda principal. Repartir sobre lo que
    // falta daría una inicial distinta según lo que ya se hubiera tecleado.
    const inicialEnPrincipal = (totales.total * porcentaje) / 100;
    const restoEnPrincipal = totales.total - inicialEnPrincipal;

    const filas: FilaPago[] = [];

    // ── La inicial ─────────────────────────────────────────────────────────
    const monedaInicial = activas.find((m) => m.currency_code === codigoPrincipal) ?? activas[0];
    const metodoInicial = elegirMetodo(metodosCobro, monedaInicial?.currency_code ?? "");
    if (inicialEnPrincipal > 0.005 && monedaInicial && metodoInicial) {
      filas.push({
        id: ++contador,
        metodoId: metodoInicial.id,
        moneda: monedaInicial.currency_code,
        monto: String(
          redondearMoneda(inicialEnPrincipal * toNumber(monedaInicial.current_rate), monedaInicial),
        ),
        datos: {},
        etiqueta: "Inicial del cliente",
      });
    }

    // ── Lo que pone la financiera ──────────────────────────────────────────
    //
    // ⚠️ En SU moneda, no en la de la inicial. Es lo que el negocio va a
    // recibir por transferencia, y anotarlo en otra moneda haría que la cuenta
    // por cobrar no cuadrara nunca con lo que llega al banco.
    const monedaResto =
      activas.find((m) => m.currency_code === metodo.allowed_currency_code) ??
      activas.find((m) => m.currency_code === codigoPrincipal) ??
      activas[0];
    if (restoEnPrincipal > 0.005 && monedaResto) {
      filas.push({
        id: ++contador,
        metodoId: metodo.id,
        moneda: monedaResto.currency_code,
        monto: String(
          redondearMoneda(restoEnPrincipal * toNumber(monedaResto.current_rate), monedaResto),
        ),
        datos: {},
      });
    }

    // Reemplaza lo que hubiera: mezclar una venta con financiera con pagos
    // sueltos de antes descuadra el reparto, y el cajero no lo vería.
    setPagos(filas);
    setInicialPct(String(porcentaje));
    setInicialObjetivo(inicialEnPrincipal);
  }

  /** Botón de un toque: cobra el total exacto en una moneda. */
  function cobrarTodoEn(moneda: TenantCurrency, metodo: PaymentMethod) {
    // ⚠️ Se pregunta con la lista de pagos VACÍA porque este botón los
    // reemplaza todos. Usar los de ahora daría el total de una venta a medio
    // pagar, y este botón cobra la venta entera de un toque.
    const enReferencia = loQueFaltaSiSePagaEn(moneda.currency_code, [], 0);
    const enMoneda = enReferencia * toNumber(moneda.current_rate);
    const redondeado = redondearMoneda(enMoneda, moneda);
    setPagos([
      {
        id: ++contador,
        metodoId: metodo.id,
        moneda: moneda.currency_code,
        monto: String(redondeado),
        datos: {},
      },
    ]);
  }

  const vender = useMutation({
    mutationFn: async (): Promise<Sale> => {
      const clientUuid = crypto.randomUUID();

      const lista: PagoIn[] = pagos
        .filter((p) => toNumber(p.monto) > 0)
        .map((p) => ({
          payment_method_id: p.metodoId,
          currency_code: p.moneda,
          amount: p.monto,
          direction: "in" as const,
          datos: p.datos,
          // Se sigue mandando `reference` por separado para que una versión
          // anterior del servidor lo entienda igual.
          reference: (p.datos.referencia ?? "").trim() || null,
        }));

      // El vuelto se envía como movimientos más, con dirección 'change': uno
      // por cada moneda en que se entrega. El servidor ya los suma todos para
      // cuadrar la venta.
      if (sobra > 0) {
        partesVuelto.forEach((p, i) => {
          const monto = montosVuelto[i] ?? 0;
          if (monto <= 0 || !p.moneda || !p.metodo) return;
          const m = activas.find((x) => x.currency_code === p.moneda);
          lista.push({
            payment_method_id: p.metodo,
            currency_code: p.moneda,
            amount: String(Number(monto.toFixed(m?.decimals ?? 2))),
            direction: "change",
          });
        });
      }

      // Cobrando algo que no es el carrito (la cuenta de una mesa): quien nos
      // llamó sabe qué se está cobrando. Aquí solo se resolvió CÓMO se paga.
      if (enviar) return enviar(lista, clientUuid, encendidos.map((t) => t.id));

      const base: VentaIn = {
        client_uuid: clientUuid,
        // En qué moneda están los precios de este aparato: si el negocio la
        // cambió sin que se enterara, el servidor convierte la venta.
        moneda: codigoPrincipal || undefined,
        items: carrito.lineas.map((l) => ({
          item_id: l.itemId,
          variant_id: l.variantId,
          qty: String(l.cantidad),
          // ⚠️ Un SÍ, no un monto. El precio de mayor lo pone el servidor
          // sacándolo del producto: uno que viniera de aquí se podría cambiar
          // desde las herramientas del navegador.
          discount: l.descuento > 0 ? String(l.descuento) : undefined,
          staff_user_id: l.staffUserId,
          // De cuánto es la recarga. El precio lo saca el servidor de aquí.
          monto: l.monto != null ? String(l.monto) : undefined,
          // Los extras: solo cuáles. Su precio también lo pone el servidor.
          opcion_ids: l.opciones?.length ? l.opciones.map((o) => o.id) : undefined,
          // «Sin tomate»: esos ingredientes no salen del inventario.
          sin: l.sin?.length ? l.sin.map((q) => q.variant_id) : undefined,
        })),
        payments: lista,
        customer_id: carrito.clienteId,
        // Con qué lista se cobra. El servidor busca el precio de cada
        // producto en esa lista y, si no tiene, usa el normal.
        lista_precios_id: carrito.listaPreciosId ?? undefined,
        // ⚠️ En qué local. Antes NUNCA se mandaba, así que todo caía en la
        // sucursal principal: se podían crear locales y las ventas de todos
        // acababan en el mismo sitio. El inventario y la caja de la sucursal
        // Norte no se movían jamás.
        location_id: sucursalParaVender(tenantId),
        note: carrito.nota.trim() || null,
        // La mercancía sale del inventario igual, pero no se entrega hasta que
        // esté pagada. Ver `credit/apartados.py`.
        es_apartado: esApartado || undefined,
        // ⚠️ Solo el código. El monto lo calcula el servidor. Ver el comentario
        // del estado `cupon` más arriba.
        codigo_descuento: cuponAplicado ? cuponAplicado.codigo : undefined,
        discount_total: carrito.descuentoGlobal > 0 ? String(carrito.descuentoGlobal) : undefined,
        // ⚠️ Solo los identificadores, igual que el cupón. El monto lo saca el
        // servidor de lo que se pagó en cada moneda: uno que viniera de aquí se
        // podría cambiar desde las herramientas del navegador.
        order_tax_ids: encendidos.length > 0 ? encendidos.map((t) => t.id) : undefined,
        /*
          ⚠️ De qué cotización venía este carrito, si venía de alguna.

          Solo sirve para marcar el presupuesto como cerrado: los precios los
          vuelve a calcular el servidor con el catálogo de hoy, exactamente
          igual que en cualquier otra venta.

          Y es lo único que hace posible la única cifra útil de la pantalla de
          cotizaciones — «de cada diez que paso, ¿cuántas se venden?». Sin esto,
          convertir una cotización sería indistinguible de tirarla a la basura.
        */
        cotizacion_id: carrito.cotizacionId ?? undefined,
      };

      /**
       * Guardar la venta en el dispositivo y devolver el recibo local.
       *
       * Al encolar SÍ enviamos la hora del dispositivo y la foto de las tasas:
       * la venta se cobró con esa tasa y a esa hora, y el servidor tiene que
       * registrarla así, no con los datos del momento en que sincronice.
       */
      const guardarLocalmente = async (): Promise<Sale> => {
        const payload: VentaIn = {
          ...base,
          /*
            ⚠️ Y el precio que se cobró aquí, línea a línea. Sin él, el servidor
            ponía los del catálogo del momento en que la venta LLEGA: si los
            precios subieron mientras no había señal, el pago no cuadraba y la
            venta se quedaba en conflicto para siempre. Ver `conElPrecioCobrado`.
          */
          items: conElPrecioCobrado(base.items, carrito.lineas, carrito.listaPreciosId),
          created_at: new Date().toISOString(),
          rates: fotoDeTasas(listaMonedas, codigoPrincipal),
        };

        const recibo = construirReciboLocal({
          clientUuid,
          lineas: carrito.lineas,
          totales,
          pagos: lista,
          monedas: listaMonedas,
          metodos,
          monedaPrincipal: codigoPrincipal,
          vendedor: { id: user?.id ?? null, nombre: user?.full_name ?? null },
          clienteId: carrito.clienteId,
          clienteNombre: carrito.clienteNombre,
          nota: carrito.nota.trim() || null,
          codigoDescuento: cuponAplicado?.codigo ?? null,
          listaPreciosId: carrito.listaPreciosId,
          // Las mismas reglas que el servidor: el teléfono cae al de la sede
          // principal; la dirección, no. Ver `contacto_de_la_sucursal`.
          telefonoSucursal:
            laSucursal()?.phone || sucursales?.find((s) => s.is_default)?.phone || null,
          direccionSucursal: laSucursal()?.address || null,
        });

        await encolarVenta({ payload, recibo });
        await refrescarContadores();
        return recibo;
      };

      // ⚠️ Corrigiendo una venta: no se vende otra, se corrige esa. Y sin
      // internet no se puede — anular la original y guardar la buena tienen
      // que pasar juntas en el servidor, no una hoy y otra cuando vuelva la
      // señal.
      if (carrito.corrigiendo) {
        if (!enLinea) {
          throw new Error("Para corregir una venta hace falta internet. Inténtalo cuando vuelva.");
        }
        return pos.corregir(carrito.corrigiendo.id, base);
      }

      // Sin conexión: directo a la cola.
      if (!enLinea) return guardarLocalmente();

      try {
        return await pos.vender(base);
      } catch (error) {
        // La conexión se cayó JUSTO al cobrar. La venta física ya ocurrió, así
        // que no puede fallar: se guarda en el dispositivo y se envía después.
        //
        // Cualquier otro error (falta un pago, el producto ya no existe) sí se
        // muestra: son cosas que el cajero tiene que corregir ahora.
        if (error instanceof SoftfiError && error.code === "network_error") {
          return guardarLocalmente();
        }
        throw error;
      }
    },

    onSuccess: (venta) => {
      if (venta.synced_at === null && venta.was_offline) {
        aviso.info(
          "Venta guardada en el dispositivo",
          "Se enviará sola cuando vuelva el internet. Ya puedes entregar el recibo.",
        );
      } else {
        aviso.exito(
          `Venta ${venta.number} registrada`,
          `Total ${venta.total} ${venta.currency_code}`,
        );
      }
      onVendido(venta);
    },

    onError: (e) => setError(mensajeDeError(e)),
  });

  // ── Estado del botón de cobrar ──────────────────────────────────────────

  // Se comprueba ANTES de mandar la venta: es mejor que el botón esté apagado a
  // que el cajero lo pulse y reciba un error del servidor con el cliente delante.
  const faltanReferencias = pagos.some((p) => {
    const metodo = metodos.find((m) => m.id === p.metodoId);
    return toNumber(p.monto) > 0 && faltanDatos(metodo, p.datos);
  });

  /*
    ═════════════════════════════════════════════════════════════════════════
     ⚠️ EL SOBRANTE SE MIDE CON LA MISMA TOLERANCIA QUE TODO LO DEMÁS
    ═════════════════════════════════════════════════════════════════════════

    Esto decía `sobra > 0` a secas, y el resto de la pantalla comparaba contra
    un `0,005` fijo. Las dos cosas estaban mal, y entre las dos dejaban la venta
    bloqueada sin explicar por qué.

    El caso, que es el más normal del país: total $9,68, el cliente paga en
    bolívares. A 56,80 son 549,824 Bs, que no se pueden entregar, así que
    Softfi le cobra 550 — y esos 550 valen $9,6831. Sobran tres milésimas de
    dólar.

    La pantalla decía «✓ Pago exacto», porque para el cartel tres milésimas no
    son nada. Pero el botón de confirmar exigía elegir en qué moneda entregar un
    vuelto de 0,003 — un vuelto que no existe, que no se puede escribir y que
    ninguna caja tiene. El cajero se quedaba con el cliente delante, la pantalla
    diciéndole que estaba todo bien, y el botón muerto sin un solo aviso.

    Ahora las dos cuentas usan el mismo umbral: si el cartel dice que cuadra, el
    botón deja cobrar.
  */
  const vueltoPendiente =
    (sobra > tolerancia &&
      (partesVuelto.length === 0 ||
        partesVuelto.some((p, i) => !p.moneda || !p.metodo || (montosVuelto[i] ?? 0) <= 0) ||
        reparto.deMas > tolerancia)) ||
    (vueltoSinEfectivo && !vueltoConfirmado);

  const puedeCobrar =
    pagos.some((p) => toNumber(p.monto) > 0) &&
    falta < tolerancia &&
    !faltanReferencias &&
    !vueltoPendiente;

  const botonCancelar = (
    <Boton variante="secundario" onClick={onCerrar} disabled={vender.isPending}>
      {comoPanel ? "Volver" : "Cancelar"}
    </Boton>
  );

  const botonCobrar = (
    <Boton
      variante="exito"
      tamano="lg"
      onClick={() => {
        setError(null);
        vender.mutate();
      }}
      cargando={vender.isPending}
      disabled={!puedeCobrar}
      icono={enLinea ? <Check size={18} /> : <WifiOff size={18} />}
      /* En el panel el botón ocupa el ancho: es lo único que se hace ahí y la
         columna es estrecha, así que un botón a la derecha se pierde. */
      className={comoPanel ? "flex-1" : undefined}
    >
      {carrito.corrigiendo
        ? `Guardar la corrección de ${carrito.corrigiendo.numero}`
        : enLinea
          ? "Confirmar venta"
          : "Cobrar sin conexión"}
    </Boton>
  );

  const cuerpo = (
    <>
      <div className="space-y-4">
        {/* ═══════════ AVISO SIN CONEXIÓN ═══════════ */}
        {!enLinea && (
          <Nota tono="aviso" icono={<WifiOff size={15} />}>
            <strong>Sin conexión.</strong> Puedes cobrar igual: la venta se guarda en este
            dispositivo con la tasa de hoy y se envía sola cuando vuelva el internet. El recibo
            se imprime al instante.
          </Nota>
        )}

        {/* ═══════════ TOTAL A COBRAR ═══════════ */}
        {/*
          ⚠️ El total y su equivalente en la otra moneda, juntos y grandes.

          Es lo que el cajero dice en voz alta, y en un país de dos monedas lo
          dice dos veces: «son diez dólares, siete mil ochocientos bolívares».
          Tenerlos separados —uno grande arriba y el otro en letra chica— obliga
          a bajar la vista entre una frase y la siguiente.
        */}
        <div className="rounded-[var(--radius-card)] bg-accent-soft px-4 py-3.5">
          <p className="text-xs text-muted">Total a cobrar</p>
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <p className="tabular text-3xl font-semibold leading-tight text-accent">
              {formatMoney(totales.total, formato)}
            </p>
            {activas
              .filter((m) => !m.is_primary && m.accepts_payment)
              .map((m) => (
                <p key={m.currency_code} className="tabular text-lg font-medium text-ink-soft">
                  {formatMoney(
                    redondearMoneda(totales.total * toNumber(m.current_rate), m),
                    comoFormatoJuntoA(m, formato),
                  )}
                </p>
              ))}
          </div>
        </div>

        {/*
          ═══════════════════════════════════════════════════════════════════
           LOS IMPUESTOS QUE SE ENCIENDEN A MANO — SOLO CUANDO NADIE MÁS LOS PINTA
          ═══════════════════════════════════════════════════════════════════

          Van JUSTO DEBAJO DEL TOTAL a propósito: encender uno mueve ese
          número, y el cajero tiene que ver las dos cosas de una mirada. Más
          abajo, entre las formas de pago, quedaría pareciendo otra forma de
          pago más.

          En la pantalla de vender esto no sale: lo dibuja el resumen del
          pedido, que está justo al lado. Ver `conInterruptoresDeImpuesto`.
        */}
        {conInterruptoresDeImpuesto && (
          <InterruptoresDeImpuesto
            impuestos={impuestosDePedido}
            encendidos={seleccionDeImpuestos}
            alAlternar={alternarImpuesto}
            exentos={exentosComoDinero(totalConCupon.exentoPorImpuesto, formato)}
          />
        )}

        {/* ═══════════ CÓMO PAGA ═══════════ */}
        {/*
          ═══════════════════════════════════════════════════════════════════
           ⚠️ LAS FORMAS DE PAGO, A LA VISTA. ANTES HABÍA QUE ABRIR UN BOTÓN.
          ═══════════════════════════════════════════════════════════════════

          Había dos cosas donde debía haber una: un «Cobro rápido» con un botón
          por moneda, y debajo un «+ Elegir otra forma de pago» que escondía
          TODO lo demás — Zelle, Pago Móvil, la tarjeta.

          Y ese botón mentía por omisión: quien no lo abriera creería que
          Softfi solo cobra en efectivo. Un cajero con gente esperando no abre
          nada para ver qué hay; usa lo que ve.

          Ahora se ven todas de una vez, cada una con lo que hay que cobrar en
          su moneda. Un toque = pagado entero con esa. Y sigue existiendo el
          camino de dividir, pero como lo que es: la excepción.

          ⚠️ El efectivo sale una vez por MONEDA y los demás una sola vez, y no
          es por simetría: en Venezuela «efectivo en dólares» y «efectivo en
          bolívares» son dos decisiones distintas que se toman veinte veces al
          día, mientras que Zelle es Zelle.
        */}
        {pagos.length === 0 && (
          <div>
            {/*
              ═══════════════════════════════════════════════════════════════
               ⚠️ UN SELECTOR ARRIBA Y ATAJOS ABAJO, NO SIETE TARJETAS IGUALES
              ═══════════════════════════════════════════════════════════════

              Antes cada método era una tarjeta del mismo tamaño: Efectivo,
              Efectivo VES, Pago Móvil, Tarjeta, Transferencia, Zelle, Binance.
              Siete filas idénticas antes de llegar al botón de cobrar, y
              ninguna se distingue de las otras aunque el efectivo se pulse
              cincuenta veces al día y Binance una vez al mes.

              Se lo dijeron al dueño y tenía razón: «me dijeron que era poco
              eficiente al seleccionar método de pago, y es verdad porque todos
              son cobros directos». Todas prometen lo mismo, así que hay que
              LEERLAS para elegir — y leer siete renglones es lo caro, no
              pulsar.

              Ahora hay dos cosas distintas:

                · El SELECTOR, arriba y grande: la vía completa. Se pulsa
                  cuando el cobro no es el de siempre.
                · Los ATAJOS, abajo y pequeños: lo que se usa a diario, sin
                  abrir nada.

              ⚠️ Y los atajos NO son del tamaño de las tarjetas de antes. El
              dueño lo pidió así —«no del mismo tamaño de ahorita porque sería
              muy feo»— y es la idea entera: si el atajo pesa lo mismo que el
              selector, no hay jerarquía y volvemos a siete cosas iguales.
            */}
            <button
              type="button"
              onClick={() => setVerTodasLasFormas((v) => !v)}
              className={cx(
                "pulsable flex w-full items-center justify-between gap-3",
                "rounded-[var(--radius-control)] border px-3.5 py-3 text-left",
                "transition-colors",
                verTodasLasFormas
                  ? "border-accent bg-accent-soft"
                  : "border-line-strong bg-surface hover:border-accent hover:bg-accent-soft",
              )}
            >
              <span className="flex min-w-0 items-center gap-2.5">
                <Wallet size={17} className="shrink-0 text-accent" />
                {/* El texto NO cambia al abrir. Un botón que se renombra a sí
                    mismo obliga a releerlo cada vez; la flecha girada ya dice
                    si está abierto. */}
                <span className="text-sm font-medium text-ink">Elegir método de pago</span>
              </span>
              <ChevronDown
                size={16}
                className={cx(
                  "shrink-0 text-muted transition-transform",
                  verTodasLasFormas && "rotate-180",
                )}
              />
            </button>

            {/* La lista entera, solo cuando se pide. */}
            {verTodasLasFormas && (
              <div
                className={cx(
                  "lista-escalonada mt-2 grid gap-2",
                  comoPanel ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2",
                )}
              >
                {formasDeCobro.map((f) => (
                  <button
                    key={f.clave}
                    type="button"
                    onClick={() => cobrarTodoEn(f.moneda, f.metodo)}
                    className={cx(
                      "pulsable flex items-center justify-between gap-3 rounded-[var(--radius-control)]",
                      "border border-line-strong bg-surface px-3.5 py-3 text-left",
                      "transition-colors hover:border-accent hover:bg-accent-soft",
                    )}
                  >
                    <span className="flex min-w-0 items-center gap-2.5">
                      <span className="shrink-0 text-muted">{f.icono}</span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-ink">
                          {f.metodo.name}
                        </span>
                        {hayVariasMonedas && (
                          <span className="block text-xs text-muted">
                            {f.moneda.currency_code}
                          </span>
                        )}
                      </span>
                    </span>
                    <span className="tabular shrink-0 text-sm font-semibold text-ink">
                      {formatMoney(
                        redondearMoneda(
                          totales.total * toNumber(f.moneda.current_rate),
                          f.moneda,
                        ),
                        {
                          code: f.moneda.currency_code,
                          symbol: f.moneda.symbol,
                          decimals: f.moneda.decimals,
                          symbol_before: f.moneda.symbol_before,
                        },
                      )}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {/*
              ⚠️ LOS ATAJOS SE ESCONDEN CON LA LISTA ABIERTA.

              Con las dos cosas a la vez, el efectivo saldría dos veces en la
              misma pantalla y a distinto tamaño — y quien vea eso se pregunta
              si son lo mismo. La lista abierta ya trae todo.
            */}
            {!verTodasLasFormas && atajos.length > 0 && (
              <div className="mt-3">
                <p className="rotulo mb-1.5">Cobro rápido</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {atajos.map((f) => (
                    <button
                      key={f.clave}
                      type="button"
                      onClick={() => cobrarTodoEn(f.moneda, f.metodo)}
                      className={cx(
                        "pulsable flex min-w-0 items-center justify-between gap-1.5",
                        "rounded-[var(--radius-control)] border border-line bg-surface-2",
                        "px-2.5 py-2 text-left",
                        "transition-colors hover:border-accent hover:bg-accent-soft",
                      )}
                    >
                      <span className="min-w-0 truncate text-xs font-medium text-ink">
                        {f.metodo.name}
                        {hayVariasMonedas && (
                          <span className="text-muted"> · {f.moneda.currency_code}</span>
                        )}
                      </span>
                      <span className="tabular shrink-0 text-xs font-semibold text-ink">
                        {formatMoney(
                          redondearMoneda(
                            totales.total * toNumber(f.moneda.current_rate),
                            f.moneda,
                          ),
                          {
                            code: f.moneda.currency_code,
                            symbol: f.moneda.symbol,
                            decimals: f.moneda.decimals,
                            symbol_before: f.moneda.symbol_before,
                          },
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══════════ PAGOS AGREGADOS ═══════════ */}
        {pagos.length > 0 && (
          <div className="relative space-y-2">
            <p className="text-sm font-medium text-ink-soft">Cómo está pagando</p>

            {/*
              ⚠️ LA INICIAL SE PREGUNTA PRIMERO, ARRIBA DE TODO.

              Estaba al final, debajo de los seis datos de Cashea y del
              «Apartar»: había que bajar una pantalla entera para llegar, y es
              lo primero que se sabe —el cliente lo enseña en su teléfono—. Con
              Cashea el porcentaje no lo decide el negocio: lo decide el nivel
              del cliente en su app, del 20 % al 60 %.

              Se esconde si el dueño lo fijó: ese negocio no quiere un campo más.
            */}
            {/*
              ⚠️ LA INICIAL SE ESCRIBE EN DÓLARES, NO EN PORCENTAJE (2026-10-01).

              Muchos negocios se quejaron: la app de Cashea le dice al cliente un
              MONTO («Inicial: $19,97»), y aquí se pedía un porcentaje entero.
              Con 26 % salía $19,66: nunca exacto, y había que adivinar. Peor si
              el cliente pagaba con punto: había que convertir a bolívares a mano.

              Ahora se escribe el monto en la moneda principal, tal cual lo dice
              la app, y Softfi pone la fila «Inicial del cliente» en SU moneda —en
              bolívares, si se cobra con punto, con la tasa del día—. El
              porcentaje queda debajo, solo como referencia.
            */}
            {financieraEnUso && !financieraEnUso.credito_inicial_fija && (
              <div className="flex items-center gap-3 rounded-[var(--radius-control)] border border-accent/30 bg-accent-soft px-4 py-3">
                <span className="min-w-0 flex-1 text-sm font-medium text-ink">
                  Inicial que paga el cliente
                  {/*
                    ⚠️ Cero es una respuesta válida y hay que decirlo: un campo
                    numérico en blanco se lee como «falta rellenar esto». El
                    nivel más alto de Cashea se lleva la mercancía sin poner nada.
                  */}
                  <span className="block text-xs font-normal text-muted">
                    Monto en {codigoPrincipal} indicado por {financieraEnUso.name} (0 si no aplica).
                  </span>
                </span>
                <div className="flex shrink-0 flex-col items-end gap-0.5">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-medium text-muted">{formato.symbol}</span>
                    <input
                      inputMode="decimal"
                      value={inicialMonto}
                      onFocus={(e) => {
                        setEditandoPct(true);
                        e.target.select();
                      }}
                      onBlur={() => setEditandoPct(false)}
                      onChange={(e) => {
                        // Con coma o con punto, como lo escriba cada quien.
                        const limpio = limpiarMonto(e.target.value, codigoPrincipal).replace(",", ".").replace(/[^\d.]/g, "");
                        setInicialMonto(limpio);
                        const n = Number(limpio);
                        if (limpio !== "" && Number.isFinite(n)) {
                          // Nunca más que el total: lo que sobre no es inicial.
                          const nueva = Math.min(totales.total, n);
                          /*
                            ⚠️ Lo que entregó el cliente se mueve con la inicial
                            SOLO si decía la inicial de antes. Si ya se escribió
                            el billete de 30, cambiar la inicial no lo borra: ese
                            dinero está en el mostrador.
                          */
                          const delMostrador = pagos
                            .filter((f) => !esCredito(f))
                            .reduce(
                              (acc, f) =>
                                acc +
                                aPrincipal(
                                  toNumber(f.monto),
                                  activas.find((m) => m.currency_code === f.moneda)?.current_rate ?? "0",
                                ),
                              0,
                            );
                          if (inicialObjetivo === null || Math.abs(delMostrador - inicialObjetivo) <= tolerancia) {
                            ajustarInicial(nueva);
                          }
                          setInicialObjetivo(nueva);
                        }
                      }}
                      aria-label={`Inicial en ${codigoPrincipal}`}
                      className={cx(
                        "tabular h-10 w-24 rounded-[var(--radius-control)] border border-line-strong",
                        "bg-surface text-center text-base font-semibold text-ink",
                      )}
                    />
                  </div>
                  {inicialPct !== "" && (
                    <span className="tabular text-xs text-muted">{inicialPct} % del total</span>
                  )}
                </div>
              </div>
            )}

            {pagos.map((p) => (
              <div
                key={p.id}
                ref={(el) => {
                  if (el) cajasDeFilas.current.set(p.id, el);
                  else cajasDeFilas.current.delete(p.id);
                }}
              >
              <FilaDePago
                fila={p}
                metodos={metodos}
                monedas={activas.filter((m) => m.accepts_payment)}
                formatoPrincipal={formato}
                montoQueCompleta={montoQueCompletaLaFila(p.moneda, p.id)}
                cliente={clienteDeLaVenta}
                onCambiar={(cambios) => cambiarFila(p, cambios)}
                onQuitar={() => setPagos((lista) => lista.filter((x) => x.id !== p.id))}
              />
              </div>
            ))}
          </div>
        )}

        {/*
          ⚠️ DIVIDIR ES LA EXCEPCIÓN, Y AHORA SE LEE COMO TAL.

          Antes este mismo botón hacía dos trabajos: con el carrito vacío decía
          «Elegir otra forma de pago» —y era la única puerta a Zelle o al Pago
          Móvil— y con algo pagado decía «Dividir». El primero ya no hace falta:
          los métodos están todos arriba.

          Queda solo el segundo, en pequeño y sin borde: pagar con dos cosas a
          la vez pasa, pero no es lo que se hace veinte veces al día.
        */}
        {pagos.length > 0 && (
          <button
            type="button"
            onClick={() => {
              const moneda = activas.find((m) => m.accepts_payment);
              const metodo = elegirMetodo(metodosCobro ?? [], moneda?.currency_code ?? "");
              if (moneda && metodo) agregarPago(metodo.id, moneda.currency_code);
            }}
            className="flex w-full items-center justify-center gap-1.5 py-1 text-sm font-medium text-muted transition-colors hover:text-accent"
          >
            <Plus size={15} />
            Dividir con otro método o moneda
          </button>
        )}

        {/*
          ═══════════════════════════════════════════════════════════════════
           FIAR — al mismo nivel que cobrar, no un paso más adentro
          ═══════════════════════════════════════════════════════════════════

          Estaba dentro de "Elegir otra forma de pago" y ahí repetía el error
          que veníamos de arreglar: había que abrir algo para descubrir que
          fiar existe. En una bodega fiar no es un caso raro — es el martes.

          Se enseña cuando todavía falta dinero, que es el único momento en
          que tiene sentido. Y cuando la venta ya tiene cliente, lo dice: así
          el cajero ve a quién le está fiando ANTES de confirmar, no después.
        */}
        {/*
          ═══════════════════════════════════════════════════════════════════
           APARTAR: LA MERCANCÍA SE QUEDA
          ═══════════════════════════════════════════════════════════════════

          ⚠️ Va ARRIBA de los métodos de pago y separado de ellos, con su
          casilla. No es una forma de cobrar: el dinero entra exactamente
          igual —un abono ahora y el resto a crédito—, lo que cambia es si la
          bolsa sale por la puerta hoy.

          Puesto entre «Efectivo» y «Zelle» se leería como otro método, y el
          cajero acabaría apartando sin querer una venta que el cliente se
          está llevando.
        */}
        {/*
          ═══════════════════════════════════════════════════════════════════
           EL CUPÓN
          ═══════════════════════════════════════════════════════════════════

          ⚠️ Se manda el CÓDIGO, nunca el monto. Lo que se ve aquí es la vista
          previa que devolvió el servidor; lo que se cobra lo vuelve a calcular
          él al guardar la venta.

          Y va escondido detrás de un enlace en vez de ocupar una casilla
          siempre visible: la inmensa mayoría de las ventas no llevan cupón, y
          un campo de texto permanente en la pantalla de cobrar es un sitio más
          donde el cajero puede quedarse escribiendo con gente esperando.
        */}
        {!enviar && (
          <div>
            {cuponAplicado ? (
              <div className="flex items-center gap-2 rounded-[var(--radius-control)] border border-success/40 bg-success-soft p-3">
                <Tag size={16} className="shrink-0 text-success" />
                <span className="min-w-0 flex-1 text-sm">
                  <span className="font-semibold text-ink">{cuponAplicado.codigo}</span>
                  <span className="text-muted">
                    {" "}
                    · −{formatMoney(cuponAplicado.descuento, formato)}
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setCuponAplicado(null);
                    setCupon("");
                    setErrorCupon(null);
                  }}
                  className="shrink-0 rounded-lg p-1.5 text-muted hover:text-ink"
                  aria-label="Quitar el cupón"
                >
                  <X size={15} />
                </button>
              </div>
            ) : mostrandoCupon ? (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Campo
                    value={cupon}
                    onChange={(e) => {
                      setCupon(e.target.value.toUpperCase());
                      setErrorCupon(null);
                    }}
                    onKeyDown={(e) => e.key === "Enter" && void aplicarCupon()}
                    placeholder="PROMO10"
                    maxLength={32}
                    autoFocus
                    className="tabular"
                  />
                  <Boton
                    variante="secundario"
                    onClick={() => void aplicarCupon()}
                    cargando={validandoCupon}
                    disabled={cupon.trim().length < 2}
                  >
                    Aplicar
                  </Boton>
                </div>
                {errorCupon && <Nota tono="peligro">{errorCupon}</Nota>}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setMostrandoCupon(true)}
                className="inline-flex items-center gap-1.5 text-sm text-muted transition-colors hover:text-accent"
              >
                <Tag size={14} />
                ¿Tiene un código de descuento?
              </button>
            )}
          </div>
        )}

        {apartadosEncendidos && !enviar && (
          <button
            type="button"
            onClick={() => {
              const siguiente = !esApartado;
              setEsApartado(siguiente);
              // Apartar exige cliente. Preguntarlo AHORA y no al confirmar:
              // descubrir que falta el cliente después de contar el dinero es
              // volver a empezar con el cliente delante.
              if (siguiente && !carrito.clienteId) setPidiendoCliente(true);
            }}
            aria-pressed={esApartado}
            className={cx(
              "flex w-full items-center gap-3 rounded-[var(--radius-control)] border p-3 text-left transition-colors",
              esApartado
                ? "border-accent bg-accent-soft"
                : "border-line hover:bg-surface-hover",
            )}
          >
            <span
              className={cx(
                "flex size-5 shrink-0 items-center justify-center rounded-[6px] border-2 transition-colors",
                esApartado ? "border-accent bg-accent text-on-accent" : "border-line-strong",
              )}
            >
              {esApartado && <Check size={13} strokeWidth={3} />}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-ink">
                Apartar
              </span>
              <span className="block text-xs text-muted">
                {esApartado
                  ? carrito.clienteNombre
                    ? `Se guarda para ${carrito.clienteNombre} hasta que termine de pagar.`
                    : "Falta decir para quién."
                  : "La mercancía se queda aquí y se entrega cuando esté pagada."}
              </span>
            </span>
          </button>
        )}

        {/*
          ═══════════════════════════════════════════════════════════════════
           ⚠️ CASHEA Y PARECIDAS: NI COBRO NI FIADO. UNA TERCERA COSA.
          ═══════════════════════════════════════════════════════════════════

          Va aparte de los métodos de cobro porque **es la operación completa,
          no una forma de pagar**: al tocarlo quedan puestas las dos filas —la
          inicial del cliente y el resto que paga la financiera—, cada una con
          su moneda.

          Y va aparte del fiado, que es lo que el dueño no podía distinguir:
          con Cashea el negocio COBRA —le paga la financiera— y con el fiado el
          negocio PRESTA. Es la diferencia entre tener el dinero en tres días o
          esperar a que el cliente vuelva. Por eso este bloque no es ámbar: no
          hay riesgo que avisar, hay un cobro que va por otro camino.

          Solo se ofrece mientras no se haya cobrado nada. Después estorbaría:
          borraría de golpe lo que el cajero ya tecleó.
        */}
        {/*
          ⚠️ EL TÍTULO Y SUS BOTONES, EN UN BLOQUE. ANTES IBAN SUELTOS.

          El título llevaba `-mb-1` —un margen NEGATIVO— para pegarse a lo de
          abajo, y estaba peleándose con el `space-y` del panel: el resultado
          era un texto flotando a distinta altura que todo lo demás. El dueño:
          «mira cómo se ve que se lo financie otro, se ve descuadrado».

          Agrupado y con `mb-2`, se comporta igual que «¿Cómo paga?», que es su
          hermano y siempre se vio bien. Un margen negativo para corregir un
          espaciado casi siempre significa que falta un contenedor.
        */}
        {pagos.length === 0 && financieras.length > 0 && (
          <div>
            {/* «O que se lo financie otro» describía la situación; «Métodos a
                crédito» nombra lo que hay debajo, que es lo que se busca
                cuando ya sabes que existe. */}
            <p className="mb-2 text-sm font-medium text-ink-soft">Métodos a crédito</p>
            <div className="space-y-2">
        {pagos.length === 0 &&
          financieras.map((f) => {
            const pct = Number(f.credito_inicial_pct);
            const inicial = (totales.total * pct) / 100;

            /*
              ⚠️ DEL MISMO TAMAÑO QUE «¿LO CARGAMOS A SU CUENTA?».

              Era una tarjeta de dos líneas con borde discontinuo, tres veces
              más alta que el botón de fiar que tiene justo debajo — y las dos
              son lo mismo: «no paga hoy». El dueño: «lo de cobrar con Cashea
              lo vas a poner al mismo tamaño de lo cargamos a la cuenta».

              Una línea: quién financia y cuánto pone el cliente ahora. El
              porcentaje se sigue pudiendo corregir justo debajo, que es donde
              importa — lo decide el nivel del cliente en su app, no nosotros.
            */
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => cobrarConFinanciera(f)}
                className={cx(
                  "flex w-full items-center justify-between gap-3 rounded-[var(--radius-control)]",
                  "border border-line px-3 py-2 text-left text-sm",
                  "text-muted transition-colors hover:border-accent hover:text-accent",
                )}
              >
                <span className="flex min-w-0 items-center gap-2">
                  <Wallet size={15} className="shrink-0" />
                  <span className="truncate">
                    {/* Corto a propósito: en el panel «Cobrar con Cashea —
                        paga 40 % ahora» se cortaba en mitad del porcentaje, que
                        es el dato que hay que leer. */}
                    {pct > 0 ? (
                      <>{f.name} · paga {pct} % ahora</>
                    ) : (
                      <>{f.name} · no paga nada ahora</>
                    )}
                  </span>
                </span>
                <span className="tabular shrink-0 font-medium">
                  {formatMoney(inicial, formato)}
                </span>
              </button>
            );
          })}
            </div>
          </div>
        )}

        {/*
          ═══════════════════════════════════════════════════════════════════
           ⚠️ FIAR NO ES UNA FORMA DE PAGAR. ES QUE HOY NO PAGA.
          ═══════════════════════════════════════════════════════════════════

          Esto era un botón suelto que decía «A crédito $10,00», del mismo
          tamaño y con el mismo aspecto que los métodos de cobro y que el de
          «Cobrar con Cashea» que tiene justo encima. Y lo dijo el dueño: la
          gente lo confunde con Cashea, o cree que va a abrir una lista para
          elegir QUÉ crédito — el del negocio o el de la financiera.

          El problema no era el texto: era que estaba en la lista equivocada.
          Cobrar con Zelle y fiar no son dos respuestas a la misma pregunta.
          «¿Cómo paga?» tiene una respuesta —efectivo, Zelle, Pago Móvil— y
          «no paga hoy» es otra pregunta distinta.

          Tuvo su propio bloque en ámbar, con título y todo, y era pasarse por
          el otro lado: **mucha gente no fía**. Un cartel de color en cada venta
          para algo que ese negocio no hace nunca es exactamente el ruido que
          hace que después nadie mire los carteles que sí importan.

          Ahora es un renglón discreto, del color del texto normal, y solo se
          pone ámbar al pasarle el ratón por encima. Está cuando hace falta y no
          pesa cuando no.

          ⚠️ Y DICE «A SU CUENTA», no «fiar». Lo pidió el socio: «fiar» suena a
          favor y deja mal al que lo pide. «Cargar a su cuenta» es lo mismo dicho
          como lo diría una tienda, y con el nombre del cliente delante —«a la
          cuenta de Jose Arellano»— porque una deuda sin nombre no se cobra nunca.

          ⚠️ Y el negocio que no fía no lo ve NUNCA: esto solo se dibuja si hay
          un método de crédito activo. Desactivándolo en Métodos de pago, el
          renglón desaparece.
        */}
        {falta > tolerancia && metodoFiado && (
          <button
            type="button"
            onClick={fiarElResto}
            className={cx(
              "flex w-full items-center justify-between gap-3 rounded-[var(--radius-control)]",
              "border border-line px-3 py-2 text-left text-sm",
              "text-muted transition-colors hover:border-warning/50 hover:text-warning",
            )}
          >
            <span className="flex min-w-0 items-center gap-2">
              <HandCoins size={15} className="shrink-0" />
              <span className="truncate">
                {esApartado ? (
                  <>Queda debiendo el resto</>
                ) : carrito.clienteNombre ? (
                  <>¿Lo cargamos a la cuenta de {carrito.clienteNombre}?</>
                ) : (
                  <>¿Lo cargamos a su cuenta?</>
                )}
              </span>
            </span>
            <span className="tabular shrink-0 font-medium">{formatMoney(falta, formato)}</span>
          </button>
        )}

        {/* ═══════════ FALTA / SOBRA ═══════════ */}
        {pagos.length > 0 && (
          <div
            className={cx(
              "rounded-[var(--radius-card)] p-4",
              falta > tolerancia
                ? "bg-danger-soft"
                : sobra > tolerancia
                  ? "bg-warning-soft"
                  : "bg-success-soft",
            )}
          >
            {/* Lo recibido hasta ahora, para que el cajero siga la cuenta */}
            <div className="mb-2 flex items-center justify-between border-b border-current/10 pb-2 text-xs">
              <span className="text-muted">Recibido</span>
              <span className="tabular font-medium text-ink">
                {formatMoney(recibido, formato)} de {formatMoney(totales.total, formato)}
              </span>
            </div>

            {falta > tolerancia ? (
              <div className="text-center">
                <p className="text-xs font-medium uppercase tracking-wide text-danger">Falta</p>
                <p className="tabular text-3xl font-semibold text-danger">
                  {formatMoney(falta, formato)}
                </p>
              </div>
            ) : sobra > tolerancia ? (
              <div className="text-center">
                <p className="text-xs font-medium uppercase tracking-wide text-warning">
                  Vuelto a entregar
                </p>
                {/*
                  ⚠️ El importe grande sale del vuelto REAL, no del sobrante
                  teórico. Antes decía «$0,02» y el billete que se entregaba eran
                  10 Bs, que son $0,0125 — dos cifras que se contradicen en la
                  misma pantalla hacen dudar de las dos.
                */}
                <p className="tabular text-3xl font-semibold text-warning">
                  {formatMoney(vueltoEnPrincipal, formato)}
                </p>
              </div>
            ) : (
              <p className="tabular flex items-center justify-center gap-2 text-xl font-semibold text-success">
                <Check size={22} /> Pago exacto
              </p>
            )}

            {/*
              LO QUE FALTA, EN CADA MONEDA.

              Es la cuenta que el cajero tendría que hacer de cabeza: "faltan 1,00
              dólar, ¿cuántos bolívares le pido?". Aquí sale ya calculado y
              redondeado como se cobra en cada moneda.
            */}
            {equivalencias.length > 0 && (
              <div className="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-1 border-t border-current/10 pt-2">
                {equivalencias.map(({ moneda: m, monto }) => (
                  <span key={m.currency_code} className="tabular text-sm text-ink-soft">
                    ={" "}
                    <strong>{formatMoney(monto, comoFormatoJuntoA(m, formato))}</strong>
                  </span>
                ))}
              </div>
            )}

            {/*
              Y si falta, se cobra el resto de un toque, sin escribir ni un
              número: con efectivo o con lo que más usa el negocio. Ver
              `formasParaElResto`.
            */}
            {falta > tolerancia && formasParaElResto.length > 0 && (
              <div className="mt-3">
                <p className="rotulo mb-1.5">Cobrar el resto</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {formasParaElResto.map((f) => {
                    const monto = montoQueCompleta(f.moneda.currency_code);
                    if (!monto) return null;

                    return (
                      <button
                        key={f.clave}
                        type="button"
                        onClick={() => agregarPago(f.metodo.id, f.moneda.currency_code, monto)}
                        className={cx(
                          "pulsable flex min-w-0 items-center justify-between gap-1.5",
                          "rounded-[var(--radius-control)] border border-current/25 bg-surface/60",
                          "px-2.5 py-2 text-left transition-colors hover:bg-surface",
                        )}
                      >
                        <span className="min-w-0 truncate text-xs font-medium text-ink">
                          {f.metodo.name}
                          {hayVariasMonedas && (
                            <span className="text-muted"> · {f.moneda.currency_code}</span>
                          )}
                        </span>
                        <span className="tabular shrink-0 text-xs font-semibold text-ink">
                          {formatMoney(monto, {
                            code: f.moneda.currency_code,
                            symbol: f.moneda.symbol,
                            decimals: f.moneda.decimals,
                            symbol_before: f.moneda.symbol_before,
                          })}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

          </div>
        )}

        {/* ═══════════ VUELTO ═══════════ */}
        {sobra > tolerancia && (
          <div className="space-y-3 rounded-[var(--radius-card)] border border-warning/40 bg-surface p-3.5">
            {vueltoSinEfectivo && (
              <Nota tono="aviso" icono={<AlertTriangle size={15} />}>
                <strong>No entró efectivo.</strong> Se cobró con {conQueSeCobro}, así que este
                vuelto saldría del efectivo de la caja. Si el monto de más es un error, cobra el
                exacto.
                <span className="mt-2.5 flex flex-wrap gap-2">
                  <Boton tamano="sm" onClick={dejarCobroExacto}>
                    Cobrar el exacto
                  </Boton>
                  <Boton
                    tamano="sm"
                    variante="secundario"
                    icono={vueltoConfirmado ? <Check size={14} /> : undefined}
                    aria-pressed={vueltoConfirmado}
                    onClick={() => setVueltoConfirmado((v) => !v)}
                  >
                    {vueltoConfirmado ? "Vuelto confirmado" : "Sí, entrego el vuelto"}
                  </Boton>
                </span>
              </Nota>
            )}

            {/*
              ═══════════════════════════════════════════════════════════════
               EL VUELTO, EN UNA O EN VARIAS MONEDAS (2026-10-12)
              ═══════════════════════════════════════════════════════════════

              Con una sola moneda se ve como siempre: en qué moneda, con qué,
              y «A entregar» en grande. Al dividir, cada parte es una fila: en
              las primeras se escribe lo que se da, y la última se calcula sola
              con lo que falta del vuelto. Lo pidió el dueño: «13,5 $, se le da
              1 $ y 0,5 en pesos… pero que se vea bien».
            */}
            {partesVuelto.length <= 1 ? (
              <>
                <p className="text-sm font-medium text-ink">Entrega del vuelto</p>
                {partesVuelto[0] && (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Etiquetado label="Moneda del vuelto">
                      <Lista
                        value={partesVuelto[0].moneda}
                        onChange={(e) => cambiarParteVuelto(partesVuelto[0]!.id, { moneda: e.target.value })}
                      >
                        <option value="" disabled>Elige…</option>
                        {monedasDeVuelto.map((m) => (
                          <option key={m.currency_code} value={m.currency_code}>
                            {m.currency_code} — {m.name}
                          </option>
                        ))}
                      </Lista>
                    </Etiquetado>

                    <Etiquetado label="Método">
                      <Lista
                        value={partesVuelto[0].metodo}
                        onChange={(e) => cambiarParteVuelto(partesVuelto[0]!.id, { metodo: e.target.value })}
                      >
                        <option value="" disabled>Elige…</option>
                        {metodosDeVueltoEn(partesVuelto[0].moneda).map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.name}
                          </option>
                        ))}
                      </Lista>
                    </Etiquetado>
                  </div>
                )}
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-ink">Vuelto en varias monedas</p>
                <div className="space-y-2">
                  {partesVuelto.map((p, i) => (
                    <ParteDeVuelto
                      key={p.id}
                      parte={p}
                      monto={montosVuelto[i] ?? 0}
                      esUltima={i === partesVuelto.length - 1}
                      monedas={monedasDeVuelto}
                      metodos={metodosDeVueltoEn(p.moneda)}
                      formatoPrincipal={formato}
                      onCambiar={(cambios) => cambiarParteVuelto(p.id, cambios)}
                      onQuitar={() => setPartesVuelto((lista) => lista.filter((x) => x.id !== p.id))}
                    />
                  ))}
                </div>
              </>
            )}

            {reparto.deMas > tolerancia && (
              <Nota tono="peligro" icono={<AlertTriangle size={15} />}>
                Los montos indicados superan el vuelto en{" "}
                <strong className="tabular">{formatMoney(reparto.deMas, formato)}</strong>.
              </Nota>
            )}

            {montosVuelto.length > 0 && montosVuelto.every((m) => m > 0) && reparto.deMas <= tolerancia && (
              <div className="rounded-[var(--radius-control)] bg-warning-soft p-3 text-center">
                <p className="text-xs text-muted">A entregar</p>
                <p className="tabular flex flex-wrap items-baseline justify-center gap-x-2 text-2xl font-semibold text-warning">
                  {partesVuelto.map((p, i) => {
                    const m = activas.find((x) => x.currency_code === p.moneda);
                    if (!m) return null;
                    return (
                      <span key={p.id} className="inline-flex items-baseline gap-2">
                        {i > 0 && <span className="text-base font-medium text-muted">y</span>}
                        {formatMoney(montosVuelto[i] ?? 0, comoFormatoJuntoA(m, formato))}
                      </span>
                    );
                  })}
                </p>
                {partesVuelto.some(
                  (p) => activas.find((x) => x.currency_code === p.moneda)?.rounding_mode === "ceil",
                ) && (
                  <p className="mt-1 text-[11px] text-muted">
                    Redondeado hacia arriba según la configuración de{" "}
                    {partesVuelto
                      .filter((p) => activas.find((x) => x.currency_code === p.moneda)?.rounding_mode === "ceil")
                      .map((p) => p.moneda)
                      .join(" y ")}
                  </p>
                )}
              </div>
            )}

            {/* Dar una parte en otra moneda: solo si hay otra en que dar vuelto. */}
            {monedasDeVuelto.length > 1 && partesVuelto.length > 0 && partesVuelto.length < 3 && (
              <button
                type="button"
                onClick={dividirVuelto}
                className="flex w-full items-center justify-center gap-1.5 py-1 text-sm font-medium text-muted transition-colors hover:text-accent"
              >
                <Plus size={15} />
                {partesVuelto.length > 1 ? "Agregar otra moneda" : "Entregar parte en otra moneda"}
              </button>
            )}
          </div>
        )}

        {/* ═══════════ AVISOS ═══════════ */}
        {faltanReferencias && (
          <Nota tono="aviso" icono={<AlertTriangle size={15} />}>
            Falta el número de referencia de uno de los pagos. Sin él no podrás verificar ese
            cobro después.
          </Nota>
        )}

        {error && <Nota tono="peligro">{error}</Nota>}
      </div>

      {/*
        Se abre solo cuando se pulsa «Fiar» sin cliente. Al elegirlo (o crearlo
        en el momento) se pone el fiado enseguida: pulsar «Fiar», elegir a
        quién, y ya está — sin un paso más de confirmación que solo serviría
        para que alguien lo olvide a medias.
      */}
      <SelectorDeCliente
        abierto={pidiendoCliente}
        cerrar={cerrarLaPregunta}
        motivo={queriaFiar ? "fiar" : "venta"}
        alElegir={(cliente) => {
          carrito.ponerCliente(cliente.id, cliente.name);
          // Solo se pone el fiado si se llegó aquí pulsando «Fiar». Si se abrió
          // porque el negocio pide los datos siempre, esto es una venta normal.
          if (queriaFiar) ponerFiado();
          setQueriaFiar(false);
        }}
      />
    </>
  );

  /*
    ⚠️ EL PANEL Y LA VENTANA COMPARTEN EL CUERPO. Lo único distinto es la caja.

    En la pantalla de vender el cobro pasa en la columna del carrito, sin abrir
    nada: el catálogo se queda al lado y se puede agregar lo que el cliente pidió
    a última hora sin cancelar la venta. En el salón, donde no hay columna, sigue
    siendo una ventana.
  */
  if (comoPanel) {
    return (
      <div className="flex flex-col gap-4">
        <div>
          <button
            type="button"
            onClick={onCerrar}
            className="mb-1 flex items-center gap-1 text-xs font-medium text-muted transition-colors hover:text-ink"
          >
            <ChevronLeft size={14} />
            {textoVolver}
          </button>
          <p className="font-semibold text-ink">Cobrar</p>
          <p className="text-xs text-muted">
            {plural(totales.cantidadUnidades, "artículo", "artículos")} ·{" "}
            {formatMoney(totales.total, formato)}
          </p>
        </div>

        {cuerpo}

        <div className="flex gap-2 pt-1">{botonCobrar}</div>
      </div>
    );
  }

  return (
    <Modal
      abierto
      onCerrar={onCerrar}
      titulo="Cobrar"
      ancho="lg"
      descripcion={`${plural(totales.cantidadUnidades, "artículo", "artículos")} · ${formatMoney(totales.total, formato)}`}
      pie={
        <>
          {botonCancelar}
          {botonCobrar}
        </>
      }
    >
      {cuerpo}
    </Modal>
  );
}

// -----------------------------------------------------------------------------

/**
 * Una parte del vuelto cuando se da en varias monedas.
 *
 * Las primeras se escriben: es lo que el cajero decide dar en esa moneda (el
 * billete de un dólar). La última no: es lo que falta del vuelto, en su moneda,
 * y se ve con el borde punteado de lo que se calcula solo — como lo que paga
 * Cashea o lo que queda debiendo.
 */
function ParteDeVuelto({
  parte,
  monto,
  esUltima,
  monedas,
  metodos,
  formatoPrincipal,
  onCambiar,
  onQuitar,
}: {
  parte: ParteDelVuelto;
  monto: number;
  esUltima: boolean;
  monedas: TenantCurrency[];
  metodos: PaymentMethod[];
  formatoPrincipal: ReturnType<typeof comoFormato>;
  onCambiar: (cambios: Partial<ParteDelVuelto>) => void;
  onQuitar: () => void;
}) {
  const moneda = monedas.find((m) => m.currency_code === parte.moneda);
  return (
    <div className="rounded-[var(--radius-control)] border border-line bg-surface-2 p-3">
      <div className="flex items-center gap-2">
        <div className="w-24 shrink-0">
          <Lista
            value={parte.moneda}
            onChange={(e) => onCambiar({ moneda: e.target.value })}
            aria-label="Moneda de esta parte del vuelto"
            className="h-10 text-sm"
          >
            {monedas.map((m) => (
              <option key={m.currency_code} value={m.currency_code}>
                {m.currency_code}
              </option>
            ))}
          </Lista>
        </div>
        <div className="min-w-0 flex-1">
          <Lista
            value={parte.metodo}
            onChange={(e) => onCambiar({ metodo: e.target.value })}
            aria-label="Método de esta parte del vuelto"
            className="h-10 text-sm"
          >
            <option value="" disabled>
              Elige…
            </option>
            {metodos.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </Lista>
        </div>
        <button
          type="button"
          onClick={onQuitar}
          aria-label="Quitar esta parte del vuelto"
          className="shrink-0 rounded-lg p-2 text-faint transition-colors hover:bg-danger-soft hover:text-danger"
        >
          <Trash2 size={15} />
        </button>
      </div>

      <div className="relative mt-2">
        <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-medium text-muted">
          {moneda?.symbol}
        </span>
        {esUltima ? (
          <div
            aria-label={`Vuelto en ${parte.moneda}`}
            className="tabular flex h-12 items-center rounded-[var(--radius-control)] border border-dashed border-warning/50 bg-surface pl-9 text-lg font-semibold text-warning"
          >
            {monto.toLocaleString("es-VE", {
              minimumFractionDigits: moneda?.decimals ?? 2,
              maximumFractionDigits: moneda?.decimals ?? 2,
            })}
          </div>
        ) : (
          <Campo
            type="number"
            value={parte.monto}
            onChange={(e) => onCambiar({ monto: e.target.value })}
            placeholder="0"
            min="0"
            moneda={moneda ? comoFormato(moneda) : null}
            inputMode="decimal"
            aria-label={`Vuelto en ${parte.moneda}`}
            className="tabular h-12 pl-9 text-lg font-semibold"
          />
        )}
      </div>

      <p className="tabular mt-1.5 text-[11px] text-muted">
        {esUltima ? "Calculado automáticamente." : "Monto entregado en esta moneda."}
        {moneda && !moneda.is_primary && monto > 0 && (
          <>
            {" "}
            = <strong className="text-ink-soft">{formatMoney(aPrincipal(monto, moneda.current_rate), formatoPrincipal)}</strong>
          </>
        )}
      </p>
    </div>
  );
}

/**
 * Elige el método de pago más natural para una moneda: primero el efectivo
 * (el de esa moneda, ej. "Efectivo Bs", o el que sirve para todas), si no otro
 * limitado a esa moneda, si no el primero que haya.
 */
function elegirMetodo(todos: PaymentMethod[], moneda: string): PaymentMethod | undefined {
  /*
    ⚠️ NUNCA UN CRÉDITO (2026-10-01). Todo lo que llama aquí busca con qué se
    paga EN EL MOMENTO: la inicial de Cashea, lo que se paga hoy de un fiado,
    «Completar». Una financiera configurada para pagar en dólares
    (`allowed_currency_code = "USD"`) era la primera «en dólares» de la lista,
    y la inicial del cliente salía a nombre de Cashea. El crédito se elige
    solo con su propio botón.

    ⚠️ EL EFECTIVO VA PRIMERO (2026-10-01, lo pidió el dueño). Antes ganaba
    cualquier método atado a la moneda, y Zelle —que solo cobra en dólares—
    le quitaba el puesto al efectivo, que sirve para todas: la inicial de
    Cashea salía en Zelle. Lo que no sea efectivo se cambia con un toque en
    el desplegable, y el monto se convierte solo (ver `cambiarFila`).
  */
  const metodos = todos.filter((m) => m.type !== "credit");
  return (
    metodos.find((m) => m.type === "cash" && m.allowed_currency_code === moneda) ??
    metodos.find((m) => m.type === "cash" && !m.allowed_currency_code) ??
    metodos.find((m) => m.allowed_currency_code === moneda) ??
    metodos.find((m) => !m.allowed_currency_code)
  );
}

function FilaDePago({
  fila,
  metodos,
  monedas: listaMonedas,
  formatoPrincipal,
  montoQueCompleta,
  onCambiar,
  onQuitar,
  cliente,
}: {
  fila: FilaPago;
  metodos: PaymentMethod[];
  monedas: TenantCurrency[];
  formatoPrincipal: ReturnType<typeof comoFormato>;
  /** Cuánto habría que poner en ESTA moneda para completar el total. "" si ya está cubierto. */
  montoQueCompleta: string;
  onCambiar: (cambios: Partial<FilaPago>) => void;
  onQuitar: () => void;
  /** El cliente de la venta, para no volver a pedir lo que ya sabemos. */
  cliente?: { phone?: string | null; tax_id?: string | null } | null;
}) {
  const formatoDe = useFormatoDeMoneda();
  const metodo = metodos.find((m) => m.id === fila.metodoId);
  /*
    ⚠️ FIADO ES SOLO EL DEL CLIENTE. Cashea NO.

    Esto decía `type === "credit"` a secas, y con eso la fila de Cashea salía
    pintada de amarillo con un «Queda debiendo» — que es exactamente lo
    contrario de lo que pasa. Con Cashea el cliente no debe nada: pagó su
    inicial y se fue. Quien queda debiendo es Cashea, y le debe AL NEGOCIO.

    Un cajero leyendo «Queda debiendo» encima del nombre de su cliente va a
    creer que le está fiando, y en un negocio que no fía eso es motivo para
    cancelar la venta y cobrarla otra vez.
  */
  const esFiado = metodo?.type === "credit" && metodo?.credito_quien_debe === "cliente";
  const esFinanciera = metodo?.type === "credit" && metodo?.credito_quien_debe === "tercero";
  const Icono = ICONOS[metodo?.type ?? "other"] ?? Wallet;
  const moneda = listaMonedas.find((m) => m.currency_code === fila.moneda);

  // Un método limitado a una moneda fija la selección.
  const monedasPosibles = metodo?.allowed_currency_code
    ? listaMonedas.filter((m) => m.currency_code === metodo.allowed_currency_code)
    : listaMonedas;

  const monto = toNumber(fila.monto);
  const enPrincipal = moneda ? aPrincipal(monto, moneda.current_rate) : 0;

  // Lo que se calcula solo: el crédito del cliente y lo que paga la financiera.
  const calculado = esFiado || esFinanciera;

  return (
    <div className="rounded-[var(--radius-control)] border border-line bg-surface-2 p-3">
      {fila.etiqueta && (
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
          {fila.etiqueta}
        </p>
      )}
      {/* Método */}
      <div className="flex items-center gap-2">
        <span className="shrink-0 text-muted">
          <Icono size={16} />
        </span>

        {/*
          ⚠️ Los `<select>` y `<input>` de Softfi llevan `w-full` en su clase base.
          Poner `w-28` encima NO gana de forma fiable (Tailwind decide el orden en
          la hoja de estilos, no el orden en que se escriben). Por eso el ancho se
          controla SIEMPRE con un div contenedor. Esto era justo lo que aplastaba
          el campo del monto y hacía invisible lo que el cajero escribía.
        */}
        <div className="min-w-0 flex-1">
          {/*
            ⚠️ La fila del fiado NO es un desplegable.

            «Fiado» ya no aparece entre Efectivo y Tarjeta —era justo lo que
            hacía que fiar fuera un acertijo— así que tampoco puede colarse por
            aquí. Y cambiar un fiado a efectivo con el desplegable no tendría
            sentido: para eso se quita la fila y se cobra normal.
          */}
          {esFiado ? (
            <span className="flex h-10 items-center gap-2 rounded-[var(--radius-control)] bg-warning-soft px-3 text-sm font-semibold text-warning">
              <HandCoins size={15} /> Queda debiendo
            </span>
          ) : esFinanciera ? (
            /*
              La financiera tampoco es un desplegable: cambiarla a efectivo
              descuadraría el reparto de la inicial que Softfi acaba de hacer.
              Para cobrar de otra forma se quita la fila y se empieza de nuevo,
              que es un toque y no deja nada a medias.
            */
            <span className="flex h-10 items-center gap-2 rounded-[var(--radius-control)] bg-accent-soft px-3 text-sm font-semibold text-accent">
              <Wallet size={15} />
              <span className="min-w-0 truncate">{metodo?.name} te lo paga</span>
            </span>
          ) : (
            <Lista
              value={fila.metodoId}
              onChange={(e) => {
                const nuevo = metodos.find((m) => m.id === e.target.value);
                onCambiar({
                  metodoId: e.target.value,
                  // Si el método nuevo solo sirve en una moneda, la cambiamos sola.
                  ...(nuevo?.allowed_currency_code
                    ? { moneda: nuevo.allowed_currency_code }
                    : {}),
                });
              }}
              aria-label="Método de pago"
              className="h-10 text-sm"
            >
              {metodos
                /*
                  Fuera solo el de fiar, que tiene su propio botón. Cashea y
                  las demás financieras SÍ se eligen aquí: para el cajero son
                  una forma de pagar más, como la tarjeta.
                */
                .filter((m) => !(m.type === "credit" && m.credito_quien_debe === "cliente"))
                .map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
            </Lista>
          )}
        </div>

        <button
          type="button"
          onClick={onQuitar}
          aria-label="Quitar este pago"
          className="shrink-0 rounded-lg p-2 text-faint transition-colors hover:bg-danger-soft hover:text-danger"
        >
          <Trash2 size={15} />
        </button>
      </div>

      {/* Moneda y monto */}
      <div className="mt-2 flex items-stretch gap-2">
        <div className="w-24 shrink-0">
          <Lista
            value={fila.moneda}
            onChange={(e) => onCambiar({ moneda: e.target.value })}
            aria-label="Moneda"
            className="h-12 text-sm"
            disabled={monedasPosibles.length === 1 || calculado}
          >
            {monedasPosibles.map((m) => (
              <option key={m.currency_code} value={m.currency_code}>
                {m.currency_code}
              </option>
            ))}
          </Lista>
        </div>

        {/* El símbolo va como PREFIJO, no encima del número: así nunca tapa lo escrito. */}
        <div className="relative min-w-0 flex-1">
          <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-medium text-muted">
            {moneda?.symbol}
          </span>
          {/*
            ⚠️ Lo que queda debiendo NO se escribe: es el total menos lo que
            pagó hoy, y lo calcula `ModalPago`. Una casilla editable aquí
            invitaba a restar de cabeza, que era justo el problema.
          */}
          {calculado ? (
            <div
              aria-label={esFiado ? `Queda debiendo en ${fila.moneda}` : `Paga ${metodo?.name} en ${fila.moneda}`}
              className={cx(
                "tabular flex h-12 items-center rounded-[var(--radius-control)] border border-dashed bg-surface pl-9 text-lg font-semibold",
                esFiado ? "border-warning/50 text-warning" : "border-accent/50 text-accent",
              )}
            >
              {toNumber(fila.monto).toLocaleString("es-VE", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          ) : (
            <Campo
              type="number"
              value={fila.monto}
              onChange={(e) => onCambiar({ monto: e.target.value })}
              placeholder={fila.pista ?? "0"}
              min="0"
              moneda={formatoDe(fila.moneda)}
              inputMode="decimal"
              aria-label={fila.pista ?? `Monto en ${fila.moneda}`}
              autoFocus={!fila.monto}
              className="tabular h-12 pl-9 text-lg font-semibold"
            />
          )}
        </div>

        {/* El botón que le ahorra la división mental al cajero */}
        {montoQueCompleta && !calculado && (
          <button
            type="button"
            onClick={() => onCambiar({ monto: montoQueCompleta })}
            title={`Dejar esta casilla en ${montoQueCompleta} ${fila.moneda} y cerrar la venta`}
            className={cx(
              "shrink-0 rounded-[var(--radius-control)] border border-accent bg-accent-soft px-3 text-xs font-semibold text-accent",
              "transition-colors hover:bg-accent hover:text-on-accent",
            )}
          >
            Completar
          </button>
        )}
      </div>

      {/* Equivalencia y tasa, siempre visibles cuando no es la moneda de referencia */}
      {moneda && !moneda.is_primary && (
        <p className="tabular mt-1.5 text-[11px] text-muted">
          {monto > 0 ? (
            <>
              = <strong className="text-ink-soft">{formatMoney(enPrincipal, formatoPrincipal)}</strong>
              {" · "}
            </>
          ) : null}
          {explicarTasa(moneda.current_rate, formatoPrincipal.code, moneda.currency_code)}
        </p>
      )}

      {calculado && (
        <p className="mt-1.5 text-[11px] text-muted">
          {esFiado
            ? "Se calcula solo: el total menos lo que pagó hoy."
            : "Se calcula solo: el total menos la inicial."}
        </p>
      )}

      {esFiado && (
        <FechaDePago
          valor={fila.datos.vence_el ?? ""}
          onCambiar={(vence) => onCambiar({ datos: { ...fila.datos, vence_el: vence } })}
        />
      )}

      <CamposDelMetodo
        metodo={metodo}
        valores={fila.datos}
        onCambiar={(datos) => onCambiar({ datos })}
        mostrarErrores={monto > 0}
        cliente={cliente}
      />
    </div>
  );
}

/**
 * Para cuándo quedó en pagar lo que debe (lo pidió el dueño, 2026-10-20).
 *
 * Antes no se preguntaba y toda venta a crédito vencía a los 30 días, aunque
 * se hubiera acordado otra cosa; la cuenta registrada a mano sí lo pedía.
 * Ahora se elige aquí, con los mismos atajos que esa ventana. Es opcional: sin
 * fecha queda como siempre, vencida a los 30 días. Viaja en los datos del pago
 * a crédito (`datos.vence_el`) y el servidor la usa para decir qué está vencido.
 */
function FechaDePago({ valor, onCambiar }: { valor: string; onCambiar: (fecha: string) => void }) {
  const hoy = fechaLocal();
  return (
    <div className="mt-3 border-t border-line pt-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted">Fecha de pago</p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <AtajosDeFecha
          etiqueta="Fecha de pago"
          valor={valor}
          onElegir={onCambiar}
          opciones={[
            { texto: "En 7 días", fecha: enDias(7) },
            { texto: "En 15 días", fecha: enDias(15) },
            { texto: "En 30 días", fecha: enDias(30) },
            { texto: "Sin fecha", fecha: "" },
          ]}
        />
        {/* El ancho, con un contenedor: ver la nota de los `<select>` más arriba. */}
        <div className="w-40">
          <Campo
            type="date"
            value={valor}
            min={hoy}
            onChange={(e) => onCambiar(e.target.value)}
            aria-label="Fecha de pago"
            className="h-9 text-sm"
          />
        </div>
      </div>
      <p className="mt-1.5 text-[11px] text-muted">
        {!valor
          ? "Sin fecha, la deuda se considera vencida a los 30 días."
          : valor === hoy
            ? "La deuda vence hoy."
            : `La deuda vence el ${fechaEnPalabras(valor)}.`}
      </p>
    </div>
  );
}
